<script setup>
// =============================================================================
// FangateListPane.vue — Stage D: 2-layer list pane (accounts → conversations)
// Layer 0: inbox overview  |  Layer 1: conversations for selected inbox
// =============================================================================

import { ref, computed, watch, onMounted, onUnmounted, reactive, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useFangateConfirm } from 'dashboard/composables/useFangateConfirm';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import InboxesAPI from 'dashboard/api/inboxes';
import { getLastMessage } from 'dashboard/helper/conversationHelper';

const store = useStore();
const { isAdmin } = useAdmin();
const { confirm } = useFangateConfirm();

// ---------------------------------------------------------------------------
// Props / Emits
// ---------------------------------------------------------------------------
const props = defineProps({
  /** All inboxes (from Vuex inboxes/getInboxes) */
  inboxes:    { type: Array, default: () => [] },
  /** Agents list (from Vuex agents/getAgents) — used in "Assign to" submenu */
  agents:     { type: Array, default: () => [] },
  /** Labels list (from Vuex labels/getLabels) — used in filter dropdown */
  labels:     { type: Array, default: () => [] },
  /** Currently selected inbox id (null = show layer 0) */
  selectedInboxId: { type: Number, default: null },
  /** Currently selected conversation id (drives chat-row active highlight) */
  selectedConversationId: { type: [Number, String], default: null },
  /** 'unread' | 'all' — which sub-group was activated in sidebar */
  mode:       { type: String, default: 'all' },
  /** 'chats' | 'channels' | 'agents' | 'label' — which content type to show */
  view:       { type: String, default: 'chats' },
  /** Active label title when view === 'label' (cross-account label view) */
  selectedLabel: { type: String, default: null },
  /** Chatwoot account id (from route param) */
  accountId:  { type: [Number, String], required: true },
  /** Map { [inboxId]: unread } — DM/chat-only unread counter per inbox.
   *  Computed in FangateLayout from Vuex+REST so it stays accurate even
   *  when we're on layer 0 (no inbox selected yet) — without this prop the
   *  Unread sub-page renders "0 accounts" because the local conversation
   *  list is empty until a user picks an inbox. */
  unreadByInbox: { type: Object, default: () => ({}) },
  /** Map { [inboxId]: unread } — group/supergroup/channel-only unread. Used
   *  when the listpane is rendering the CHANNELS overview, so an unread DM
   *  doesn't show as unread in the channels view (and vice versa). */
  unreadByInboxChannels: { type: Object, default: () => ({}) },
  /** Map { [inboxId]: total open chats } — same source, used for stats. */
  totalByInbox:  { type: Object, default: () => ({}) },
  /** Map { [inboxId]: total } — group/supergroup/channel-only total. */
  totalByInboxChannels: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['select-inbox', 'select-conversation', 'back', 'open-agent-picker', 'new-chat', 'select-label', 'select-view']);

// ---------------------------------------------------------------------------
// Layer slide
// ---------------------------------------------------------------------------
// In the cross-account LABEL view we deliberately keep selectedInboxId set
// (so the sidebar highlights the open chat's account) WITHOUT sliding to the
// per-inbox chat list — the label list lives on layer 0.
const currentLayer = computed(() =>
  (props.view !== 'label' && props.view !== 'labels-overview' && props.selectedInboxId) ? 1 : 0
);
const slideClass    = computed(() => (currentLayer.value === 1 ? 'show-chats' : ''));

// ---------------------------------------------------------------------------
// Conversations for selected inbox
// ---------------------------------------------------------------------------
// PRIMARY source: Vuex's getAllConversations getter. Updates instantly when
// Chatwoot commits ADD_MESSAGE / UPDATE_MESSAGE_UNREAD_COUNT / etc., so
// per-conversation unread pills clear the same frame the user reads.
//
// FALLBACK source: a snapshot from one REST call per inbox change, kept
// only for the brief window before Vuex has the inbox's conversations
// loaded. Once Vuex returns something for this inbox, the snapshot is
// effectively ignored.
const convsLoading       = ref(false);
const conversationsRest  = ref([]);    // bootstrap snapshot per inbox

const fetchConversations = async inboxId => {
  if (!inboxId) return;
  convsLoading.value  = true;
  conversationsRest.value = [];
  try {
    // Fetch ALL statuses, not just open — the Resolved sub-section needs
    // resolved conversations too, and a UI bug that resolves everything
    // would leave the user with an empty list otherwise.
    const resp = await ConversationAPI.get({
      inboxId,
      assigneeType: 'all',
      status: 'all',
      page: 1,
    });
    const list = resp?.data?.data?.payload ?? [];
    // Commit every fetched conversation into Vuex. This is what makes
    // Chatwoot's read/mute/etc. mutations actually take effect on these
    // rows — without this step, the conversations would only exist in our
    // local snapshot and UPDATE_MESSAGE_UNREAD_COUNT etc. would target
    // nothing, leaving per-conv unread pills stuck at their fetched value.
    for (const conv of list) {
      if (conv?.id != null) store.commit('ADD_CONVERSATION', conv);
    }
    // Still cache locally as a bootstrap so the list renders immediately
    // even before Vuex finishes any catch-up commits.
    conversationsRest.value = list;
  } catch (_) {
    conversationsRest.value = [];
  } finally {
    convsLoading.value = false;
  }
};

// Live list — filter Vuex's getAllConversations by the active inbox.
// Falls back to the REST snapshot only when Vuex hasn't picked up any
// conversations for this inbox yet (cold start).
const conversations = computed(() => {
  const inboxId = props.selectedInboxId;
  if (!inboxId) return [];
  const live = (store.getters.getAllConversations || [])
    .filter(c => Number(c?.inbox_id) === Number(inboxId));
  if (live.length) return live;
  return conversationsRest.value;
});

// chat_type discriminator (persisted by the backend in
// conversation.additional_attributes.chat_type). group/supergroup/channel
// → CHANNELS section; private / nil (legacy) → CHATS section.
const isChannelConv = (c) => {
  const t = c?.additional_attributes?.chat_type;
  return t === 'group' || t === 'supergroup' || t === 'channel';
};

// Private DMs only — what the CHATS view shows. Legacy conversations with
// no chat_type are treated as private.
const privateConversations = computed(() =>
  conversations.value.filter(c => !isChannelConv(c))
);

// Group / supergroup / broadcast-channel containers — what the CHANNELS
// view shows.
const channelConversations = computed(() =>
  conversations.value.filter(c => isChannelConv(c))
);

watch(() => props.selectedInboxId, id => { if (id) fetchConversations(id); }, { immediate: true });

// ---------------------------------------------------------------------------
// Label view — cross-account conversations carrying a given label
// ---------------------------------------------------------------------------
// Fetched server-side WITHOUT an inbox_id, so it spans every account the user
// can read; the ACL is enforced server-side (agents only get conversations in
// their own inboxes). Committed into Vuex so read/label mutations apply.
// Pagination "light": show an initial window, reveal LOAD_STEP more per click.
const INITIAL_SHOW = 20;
const LOAD_STEP    = 15;
const LABEL_PAGE_SIZE = 25;   // Chatwoot's default conversations page size

const labelConvsLoading = ref(false);
const labelConvsRest    = ref([]);
const labelPage         = ref(1);
const labelHasMore      = ref(false);   // server has further pages beyond what's loaded

// `append:false` = fresh load (reset to page 1); `append:true` = next page via
// the "Load more" button. Each page is committed into Vuex so read/label
// mutations apply, and labelHasMore tracks whether the server likely has more.
const fetchLabelConversations = async (label, { append = false } = {}) => {
  if (!label) { labelConvsRest.value = []; labelHasMore.value = false; return; }
  labelConvsLoading.value = true;
  if (!append) { labelConvsRest.value = []; labelPage.value = 1; labelHasMore.value = false; }
  try {
    const resp = await ConversationAPI.get({
      labels: [label],
      assigneeType: 'all',
      status: 'all',
      page: labelPage.value,
    });
    const list = resp?.data?.data?.payload ?? [];
    for (const conv of list) {
      if (conv?.id != null) store.commit('ADD_CONVERSATION', conv);
    }
    labelConvsRest.value = append ? [...labelConvsRest.value, ...list] : list;
    labelHasMore.value   = list.length >= LABEL_PAGE_SIZE;
  } catch (_) {
    if (!append) labelConvsRest.value = [];
  } finally {
    labelConvsLoading.value = false;
  }
};

// Live list: Vuex conversations carrying the label (so read/label changes show
// instantly), falling back to the fetched snapshot on cold start.
const labelConversations = computed(() => {
  const label = props.selectedLabel;
  if (!label) return [];
  const live = (store.getters.getAllConversations || [])
    .filter(c => (c?.labels || []).includes(label));
  return live.length ? live : labelConvsRest.value;
});

// Sort for the drill-in: Activity (most recent first, default) | Alphabetical.
const labelSort = ref('activity');   // 'activity' | 'alpha'
const sortedLabelConversations = computed(() => {
  const list = [...labelConversations.value];
  if (labelSort.value === 'alpha') {
    return list.sort((a, b) => convName(a).localeCompare(convName(b), undefined, { sensitivity: 'base' }));
  }
  return list.sort((a, b) => (b.last_activity_at || b.created_at || 0) - (a.last_activity_at || a.created_at || 0));
});

// Description of the active label (shown in the drill-in header) — looked up by
// title from the labels list, since the view only receives the title string.
const selectedLabelDescription = computed(() => {
  if (!props.selectedLabel) return '';
  return (props.labels || []).find(l => l.title === props.selectedLabel)?.description || '';
});

// Type filter for the label drill-in: All / Chats / Channels / Groups.
const labelTypeTab = ref('all');
const filteredLabelConversations = computed(() => {
  const tab = labelTypeTab.value;
  if (tab === 'all') return sortedLabelConversations.value;
  const want = tab === 'chats' ? 'chat' : (tab === 'channels' ? 'channel' : 'group');
  return sortedLabelConversations.value.filter(c => convTypeKey(c) === want);
});

// Paged window + load-more for the label drill-in. Revealing more first shows
// already-loaded rows; once those run out and the server has more, it fetches
// the next page.
const labelVisible = ref(INITIAL_SHOW);
const pagedLabelConversations = computed(() => filteredLabelConversations.value.slice(0, labelVisible.value));
const labelHasMoreToShow = computed(() =>
  labelVisible.value < filteredLabelConversations.value.length || labelHasMore.value
);
const loadMoreLabelConvs = async () => {
  if (labelConvsLoading.value) return;
  labelVisible.value += LOAD_STEP;
  if (labelVisible.value > labelConversations.value.length && labelHasMore.value) {
    labelPage.value += 1;
    await fetchLabelConversations(props.selectedLabel, { append: true });
  }
};

watch(() => props.selectedLabel, label => {
  labelTypeTab.value = 'all';
  labelVisible.value = INITIAL_SHOW;
  fetchLabelConversations(label);
}, { immediate: true });
watch([labelSort, labelTypeTab], () => { labelVisible.value = INITIAL_SHOW; });

// Display helpers for label-view rows (cross-account, so we show the account).
const convName = conv =>
  conv?.meta?.sender?.name || conv?.contact?.name || 'Unknown';
const inboxNameOf = conv =>
  (props.inboxes.find(i => Number(i.id) === Number(conv?.inbox_id))?.name) || 'Account';
// Conversation kind for the label view. Telegram's chat_type is persisted in
// additional_attributes by IncomingMessageService: channel → Channel,
// group/supergroup → Group, private/missing → Chat.
const convTypeKey = conv => {
  const t = conv?.additional_attributes?.chat_type;
  if (t === 'channel') return 'channel';
  if (t === 'group' || t === 'supergroup') return 'group';
  return 'chat';
};
const convType = conv => ({ chat: 'Chat', group: 'Group', channel: 'Channel' }[convTypeKey(conv)]);

// ---------------------------------------------------------------------------
// Layer 0 — inbox list helpers
// ---------------------------------------------------------------------------
const accSearch  = ref('');
const accSort    = ref('default');       // 'default' | 'alpha' | 'unread'
const accSortOpen = ref(false);

// Channels & Groups info panel — explains when a Telegram bot can read
// group/channel messages and how to set it up (admin / privacy-mode). Opened
// via the "i" button in the Channels & Groups accounts-overview header.
const showChannelsInfo = ref(false);
const ACC_SORT_OPTIONS = [
  { id: 'default', label: 'Default' },
  { id: 'alpha',   label: 'Alphabetical' },
  { id: 'unread',  label: 'Unread first' },
];

/** Inboxes enriched with unread / total counts from parent-supplied maps.
 *  Layout-level computation (uses Vuex + REST) so it's accurate even on
 *  layer 0 where the local `conversations` array is empty.
 *  In the CHANNELS view we use the channel-only counts so an unread DM
 *  doesn't show as unread for an account in the channels overview. */
const inboxesWithStats = computed(() => {
  const isChannelsView = props.view === 'channels';
  const unreadMap = isChannelsView ? props.unreadByInboxChannels : props.unreadByInbox;
  const totalMap  = isChannelsView ? props.totalByInboxChannels  : props.totalByInbox;
  return props.inboxes.map(inbox => ({
    ...inbox,
    unreadCount: unreadMap[inbox.id] || 0,
    totalChats:  totalMap[inbox.id]  || 0,
  }));
});

const filteredInboxes = computed(() => {
  let list = inboxesWithStats.value;
  if (props.mode === 'unread') list = list.filter(i => i.unreadCount > 0);
  if (accSearch.value) {
    const q = accSearch.value.toLowerCase();
    list = list.filter(i => i.name.toLowerCase().includes(q));
  }
  if (accSort.value === 'alpha') {
    list = [...list].sort((a, b) => a.name.localeCompare(b.name));
  } else if (accSort.value === 'unread') {
    list = [...list].sort((a, b) => b.unreadCount - a.unreadCount);
  } else {
    // Default: mirror the sidebar's two-band sort — accounts with unread
    // float to the top (sorted by unread count desc so the most-active rises
    // first), accounts without unread are alphabetical underneath. Was an
    // identity sort before, so the listpane order didn't match the sidebar.
    list = [...list].sort((a, b) => {
      const aU = a.unreadCount > 0 ? 1 : 0;
      const bU = b.unreadCount > 0 ? 1 : 0;
      if (aU !== bU) return bU - aU;
      if (aU && bU) {
        const diff = (b.unreadCount || 0) - (a.unreadCount || 0);
        if (diff !== 0) return diff;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }
  return list;
});

const layer0Title    = computed(() => (props.mode === 'unread' ? 'Unread' : 'All'));
const layer0Subtitle = computed(() =>
  props.mode === 'unread'
    ? 'Accounts with unread messages'
    : 'All linked accounts'
);
const totalUnreadAccs = computed(() =>
  inboxesWithStats.value.filter(i => i.unreadCount > 0).length
);

// ---------------------------------------------------------------------------
// Layer 1 — conversation list helpers
// ---------------------------------------------------------------------------
const chatTab     = ref('all');       // 'all' | 'unread'
const chatSearch  = ref('');
const chatSortOpen   = ref(false);
const chatFilterOpen = ref(false);
const chatSort    = ref('default');
const CHAT_SORT_OPTIONS = [
  { id: 'default', label: 'Newest first' },
  { id: 'oldest',  label: 'Oldest first' },
  { id: 'unread',  label: 'Unread first' },
];

const CHAT_FILTER_OPTIONS = [
  // Read/Unread status intentionally omitted here — it already lives in the
  // ALL / Unread tabs above the list (chatTab), so duplicating it as a filter
  // would be two controls for one thing.
  { id: 'today',     label: 'Active today',    group: 'Activity' },
  { id: 'yesterday', label: 'Active yesterday',group: 'Activity' },
  { id: 'week',      label: 'Last 7 days',     group: 'Activity' },
];
const activeFilters  = reactive(new Set());
const filterActive   = computed(() => activeFilters.size > 0);
const filterGroups   = computed(() => {
  const grps = {};
  CHAT_FILTER_OPTIONS.forEach(f => { (grps[f.group] ??= []).push(f); });
  return Object.entries(grps);
});
const toggleChatFilter = id => {
  if (activeFilters.has(id)) activeFilters.delete(id);
  else activeFilters.add(id);
};
const clearAllFilters = () => { activeFilters.clear(); };

// Convert active filter IDs → display chips with human-readable labels
const activeFilterChips = computed(() => {
  return [...activeFilters].map(id => {
    if (id.startsWith('agent:')) {
      const aid = Number(id.slice(6));
      const agent = props.agents.find(a => a.id === aid);
      return { id, label: '👤 ' + (agent?.name ?? 'Agent') };
    }
    if (id.startsWith('label:')) {
      return { id, label: '🏷 ' + id.slice(6) };
    }
    const opt = CHAT_FILTER_OPTIONS.find(o => o.id === id);
    return { id, label: opt?.label ?? id };
  });
});

// Resolve tracking. Local Set gives instant feedback on Resolve clicks;
// the server-side conversation.status field gives persistence across
// reloads. A conv counts as "resolved" if EITHER channel says so.
const resolvedConvIds = reactive(new Set());
const resolvedSecOpen = ref(false);
const isResolvedConv = c =>
  resolvedConvIds.has(c.id) ||
  c.status === 'resolved' ||
  c.status === 1;
const resolvedList = computed(() =>
  conversations.value.filter(c => isResolvedConv(c))
);

const selectedInbox = computed(() =>
  props.inboxes.find(i => i.id === props.selectedInboxId) ?? null
);

// ---- Persona-name auto-fit ---------------------------------------------------
// Shrink the account-name font (max → min) so long names stay readable; only
// below the minimum does it fall back to the ellipsis. Re-runs when the name
// changes and when the list pane is resized (column drag).
const nameRef = ref(null);
const titleRowRef = ref(null);
const fitName = () => {
  const el = nameRef.value;
  if (!el) return;
  const MAX = 15;
  const MIN = 11;
  let size = MAX;
  el.style.fontSize = `${size}px`;
  let guard = 0;
  while (el.scrollWidth > el.clientWidth + 0.5 && size > MIN && guard < 24) {
    size -= 0.5;
    el.style.fontSize = `${size}px`;
    guard += 1;
  }
};
let nameResizeObserver = null;
watch(titleRowRef, el => {
  if (nameResizeObserver) { nameResizeObserver.disconnect(); nameResizeObserver = null; }
  if (el && typeof ResizeObserver !== 'undefined') {
    nameResizeObserver = new ResizeObserver(() => fitName());
    nameResizeObserver.observe(el);
  }
  nextTick(fitName);
});
watch(() => selectedInbox.value?.name, () => nextTick(fitName));
onUnmounted(() => { if (nameResizeObserver) nameResizeObserver.disconnect(); });

// ---------------------------------------------------------------------------
// Channels list (per account)
//
// Group / supergroup / broadcast-channel conversations the bot is a member
// or admin of. These come through the same Vuex conversation store as
// private chats — the backend tags each with chat_type, and we route
// group/supergroup/channel here (see channelConversations above).
//
// Each row is a real conversation, so clicking one opens it in the chat
// area exactly like a private DM (same select-conversation emit path).
// ---------------------------------------------------------------------------
const channelSearch = ref('');
const channelList = computed(() => {
  if (!props.selectedInboxId) return [];
  let list = channelConversations.value.filter(c =>
    !isResolvedConv(c)
    && !deletedConvIds.has(c.id)
    && c.status !== 2 && c.status !== 'pending'
    && c.status !== 3 && c.status !== 'snoozed'
  );
  if (channelSearch.value) {
    const q = channelSearch.value.toLowerCase();
    list = list.filter(c => {
      const name = c?.meta?.sender?.name || '';
      return name.toLowerCase().includes(q);
    });
  }
  // Newest activity first — same ordering as the chats list.
  return [...list].sort((a, b) =>
    (b.last_activity_at || b.created_at || 0) - (a.last_activity_at || a.created_at || 0)
  );
});

// Apply the activity / agent / label filters (the shape shared by the chats and
// channels drill-ins) to a conversation list. Search, tab and sort are
// view-specific and applied by the caller. Kept standalone so the channels
// filter reuses identical semantics without touching the chats path.
const applyConvFilters = (list, filterSet) => {
  let out = list;
  if (filterSet.has('today')) {
    const today = new Date().toDateString();
    out = out.filter(c => new Date((c.last_activity_at || c.created_at) * 1000).toDateString() === today);
  }
  if (filterSet.has('yesterday')) {
    const yd = new Date(Date.now() - 86400000).toDateString();
    out = out.filter(c => new Date((c.last_activity_at || c.created_at) * 1000).toDateString() === yd);
  }
  if (filterSet.has('week')) {
    const wAgo = Date.now() - 7 * 86400000;
    out = out.filter(c => (c.last_activity_at || c.created_at) * 1000 >= wAgo);
  }
  const agentFilters = [...filterSet].filter(f => f.startsWith('agent:'));
  if (agentFilters.length) {
    const wantedIds = new Set(agentFilters.map(f => Number(f.slice(6))));
    out = out.filter(c => wantedIds.has(c.meta?.assignee?.id));
  }
  const labelFilters = [...filterSet].filter(f => f.startsWith('label:'));
  if (labelFilters.length) {
    const wantedLabels = labelFilters.map(f => f.slice(6));
    out = out.filter(c => {
      const cLabels = c.labels || [];
      return wantedLabels.every(lbl => cLabels.includes(lbl));
    });
  }
  return out;
};

// ---- Channels drill-in: type tabs + its OWN filter/sort --------------------
// Deliberately INDEPENDENT of the chats list's filter/sort (separate refs), so
// filtering channels never disturbs the chats view and vice versa.
const channelTypeTab  = ref('all');            // 'all' | 'channels' | 'groups'
const chFilterOpen    = ref(false);
const chSortOpen      = ref(false);
const chActiveFilters = reactive(new Set());   // 'today'/'week'/'agent:<id>'/'label:<title>' …
const chSort          = ref('default');        // 'default' (newest) | 'oldest' | 'unread'
const chFilterActive  = computed(() => chActiveFilters.size > 0);
const toggleChFilter  = id => { if (chActiveFilters.has(id)) chActiveFilters.delete(id); else chActiveFilters.add(id); };
const clearChFilters  = () => { chActiveFilters.clear(); };

const displayedChannels = computed(() => {
  let list = channelList.value;  // already search-filtered + newest-first
  if (channelTypeTab.value === 'channels') {
    list = list.filter(c => c?.additional_attributes?.chat_type === 'channel');
  } else if (channelTypeTab.value === 'groups') {
    list = list.filter(c => {
      const t = c?.additional_attributes?.chat_type;
      return t === 'group' || t === 'supergroup';
    });
  }
  list = applyConvFilters(list, chActiveFilters);
  if (chSort.value === 'oldest') {
    list = [...list].sort((a, b) => (a.last_activity_at || a.created_at || 0) - (b.last_activity_at || b.created_at || 0));
  } else if (chSort.value === 'unread') {
    list = [...list].sort((a, b) => notifyUnreadOf(b) - notifyUnreadOf(a));
  }
  return list;
});

// Paged window + load-more for the CHANNELS list. Resets on context change
// (account / tab / sort / filter / search) but not on live data updates.
const channelsVisible = ref(INITIAL_SHOW);
const pagedChannels   = computed(() => displayedChannels.value.slice(0, channelsVisible.value));
const channelsHasMore = computed(() => displayedChannels.value.length > channelsVisible.value);
const loadMoreChannels = () => { channelsVisible.value += LOAD_STEP; };
watch(
  [() => props.selectedInboxId, channelTypeTab, chSort, channelSearch, () => [...chActiveFilters].join(',')],
  () => { channelsVisible.value = INITIAL_SHOW; }
);

const filteredConversations = computed(() => {
  // Hide resolved (server status OR local toggle), blocked, deleted, and
  // pending/snoozed from the main list. Each has its own section / is
  // simply not displayed. CHATS view shows private DMs only — group /
  // channel containers are routed to the CHANNELS section.
  let list = privateConversations.value.filter(c =>
    !isResolvedConv(c)
    && !isContactBlocked(c)
    && !deletedConvIds.has(c.id)
    && c.status !== 2 && c.status !== 'pending'
    && c.status !== 3 && c.status !== 'snoozed'
  );
  if (chatTab.value === 'unread') list = list.filter(c => notifyUnreadOf(c) > 0);
  if (activeFilters.has('today')) {
    const today = new Date().toDateString();
    list = list.filter(c => new Date((c.last_activity_at || c.created_at) * 1000).toDateString() === today);
  }
  if (activeFilters.has('yesterday')) {
    const yd = new Date(Date.now() - 86400000).toDateString();
    list = list.filter(c => new Date((c.last_activity_at || c.created_at) * 1000).toDateString() === yd);
  }
  if (activeFilters.has('week')) {
    const wAgo = Date.now() - 7 * 86400000;
    list = list.filter(c => (c.last_activity_at || c.created_at) * 1000 >= wAgo);
  }
  // Agent filters (prefix "agent:<id>") — match conv's assignee.id
  const agentFilters = [...activeFilters].filter(f => f.startsWith('agent:'));
  if (agentFilters.length) {
    const wantedIds = new Set(agentFilters.map(f => Number(f.slice(6))));
    list = list.filter(c => wantedIds.has(c.meta?.assignee?.id));
  }
  // Label filters (prefix "label:<title>") — match conv's labels array (titles)
  const labelFilters = [...activeFilters].filter(f => f.startsWith('label:'));
  if (labelFilters.length) {
    const wantedLabels = labelFilters.map(f => f.slice(6));
    list = list.filter(c => {
      const cLabels = c.labels || [];
      return wantedLabels.every(lbl => cLabels.includes(lbl));
    });
  }
  if (chatSearch.value) {
    const q = chatSearch.value.toLowerCase();
    list = list.filter(c =>
      (c.meta?.sender?.name || '').toLowerCase().includes(q)
    );
  }
  if (chatSort.value === 'oldest') list = [...list].sort((a, b) => a.created_at - b.created_at);
  if (chatSort.value === 'unread') list = [...list].sort((a, b) => notifyUnreadOf(b) - notifyUnreadOf(a));
  return list;
});

// Paged window + load-more for the CHATS list.
const chatsVisible   = ref(INITIAL_SHOW);
const pagedConversations = computed(() => filteredConversations.value.slice(0, chatsVisible.value));
const chatsHasMore   = computed(() => filteredConversations.value.length > chatsVisible.value);
const loadMoreChats  = () => { chatsVisible.value += LOAD_STEP; };

// MTProto: the chat list only holds dialogs imported so far. Once the local
// "Load more" is exhausted, this pulls the next batch of older Telegram dialogs
// (last message of each) into the list. Serves both CHATS and CHANNELS — the
// dialog import covers DMs, groups and channels — then refetches conversations.
const isMtprotoListInbox = computed(() =>
  selectedInbox.value?.channel_type === 'Channel::TelegramUser'
);
const loadingTgDialogs = ref(false);
const tgDialogWant     = ref(INITIAL_SHOW);
const loadOlderTelegramChats = async () => {
  if (loadingTgDialogs.value || !props.selectedInboxId) return;
  loadingTgDialogs.value = true;
  try {
    tgDialogWant.value += INITIAL_SHOW;   // ask Telegram for a bigger window
    await InboxesAPI.loadMtprotoDialogs(props.selectedInboxId, tgDialogWant.value);
    await fetchConversations(props.selectedInboxId);
  } catch (_) {
    /* sidecar unavailable → leave the list as-is */
  } finally {
    loadingTgDialogs.value = false;
  }
};
// Reset the Telegram window when the inbox changes.
watch(() => props.selectedInboxId, () => { tgDialogWant.value = INITIAL_SHOW; });
watch(
  [() => props.selectedInboxId, chatTab, chatSort, chatSearch, () => [...activeFilters].join(',')],
  () => { chatsVisible.value = INITIAL_SHOW; }
);

// Paged window + load-more for the LABELS overview (sorted alphabetically upstream).
const labelsVisible  = ref(INITIAL_SHOW);
const pagedLabels    = computed(() => (props.labels || []).slice(0, labelsVisible.value));
const labelsHasMore  = computed(() => (props.labels || []).length > labelsVisible.value);
const loadMoreLabels = () => { labelsVisible.value += LOAD_STEP; };
watch(() => props.view, v => { if (v === 'labels-overview') labelsVisible.value = INITIAL_SHOW; });

// Chats header counts cover private DMs only — channel/group containers
// have their own section, so they shouldn't inflate the CHATS totals.
const totalChats   = computed(() => privateConversations.value.length);
const unreadChats  = computed(() => privateConversations.value.filter(c => notifyUnreadOf(c) > 0).length);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getInitials = name => (name ? name.charAt(0).toUpperCase() : '?');

const formatTime = ts => {
  if (!ts) return '';
  const d   = new Date(ts * 1000);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
};

// Close dropdowns on outside click
const closeDropdowns = () => {
  accSortOpen.value  = false;
  chatSortOpen.value = false;
  chatFilterOpen.value = false;
  chFilterOpen.value = false;
  chSortOpen.value   = false;
};
onMounted(() => {
  document.addEventListener('click', closeDropdowns);
  document.addEventListener('click', closeChatCtxOnOutside);
});
onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns);
  document.removeEventListener('click', closeChatCtxOnOutside);
});

// ---------------------------------------------------------------------------
// Chat context menu
// ---------------------------------------------------------------------------
const chatCtx = reactive({
  show: false, x: 0, y: 0,
  conv: null,
  agentSubShow: false,   // "Assign to" submenu
  // Additional agents on this chat (conversation participants). Lazily fetched
  // when the submenu opens so the list reflects the live state.
  participantIds: [],
  participantsLoading: false,
});

// Only real agents are assignable from the context menu — admins always have
// full access to every chat anyway, so listing them as "assignees" is noise.
const assignableAgents = computed(() =>
  (props.agents || []).filter(a => a.role !== 'administrator')
);

// Open/close the "Assign to" submenu; on open, pull the current participants so
// each row can show its add-state.
const ctxToggleAgentSub = async () => {
  chatCtx.agentSubShow = !chatCtx.agentSubShow;
  if (!chatCtx.agentSubShow || !chatCtx.conv) return;
  chatCtx.participantsLoading = true;
  try {
    const { data } = await ConversationAPI.fetchParticipants(chatCtx.conv.id);
    chatCtx.participantIds = (data || []).map(p => p.id);
  } catch (_) {
    chatCtx.participantIds = [];
  } finally {
    chatCtx.participantsLoading = false;
  }
};

const openChatCtx = (e, conv) => {
  e.preventDefault();
  e.stopPropagation();
  chatCtx.show = true;
  chatCtx.conv = conv;
  chatCtx.agentSubShow = false;
  const vw = window.innerWidth, vh = window.innerHeight;
  const mw = 192, mh = 310;
  chatCtx.x = e.clientX + mw > vw ? vw - mw - 8 : e.clientX;
  chatCtx.y = e.clientY + mh > vh ? vh - mh - 8 : e.clientY;
};
const closeChatCtx = () => {
  chatCtx.show = false;
  chatCtx.conv = null;
  chatCtx.agentSubShow = false;
};

// Helpers
const isResolved = conv => resolvedConvIds.has(conv?.id);
const isMuted    = conv => !!(conv?.muted);
// Last outgoing message status (sent / delivered / read).
// Uses Chatwoot's getLastMessage helper which checks both
// conv.last_non_activity_message AND conv.messages array.
const statusTick = conv => {
  if (!conv) return null;
  try {
    const m = (conv.messages && conv.messages.length)
      ? getLastMessage(conv)
      : conv.last_non_activity_message;
    if (!m || m.message_type !== 1) return null;  // outgoing only (type 1)
    return m.status ?? null;
  } catch (_) { return null; }
};
// Contact presence (MTProto user-account). Stored on the contact's
// additional_attributes (telegram_presence); presenceById layers live
// contact.updated pushes on top so the dot flips without a list refetch.
const presenceById = reactive({});
const presenceOf = conv => {
  const s = conv?.meta?.sender;
  if (!s) return null;
  const live = presenceById[s.id];
  return live !== undefined ? live : (s.additional_attributes?.telegram_presence ?? null);
};
const isOnline = conv => presenceOf(conv) === 'online';
let unsubPresence = null;
onMounted(() => {
  unsubPresence = store.subscribe(mutation => {
    if (mutation?.type !== 'contacts/EDIT_CONTACT') return;
    const c = mutation.payload;
    if (c && c.id != null) presenceById[c.id] = c.additional_attributes?.telegram_presence ?? null;
  });
});
onUnmounted(() => { if (unsubPresence) unsubPresence(); });

// Draft preview — reads the per-conv typed-but-not-sent text stashed in
// localStorage by FangateChatArea. The chat-list row shows it ahead of
// the last-message preview so the agent knows there's unfinished work
// on that thread. draftsTick is bumped by the listeners below whenever the
// drafts actually change, which is what makes getDraft re-run.
// Event-driven (not polled): the chat area fires 'fangate-drafts-changed' when
// it writes a draft in this tab; 'storage' covers other tabs; 'focus' catches
// anything we missed while the window was in the background. This replaces a
// 1s setInterval that forced the whole conversation list to re-render every
// second (heavy churn + suspected input interference).
const draftsTick = ref(0);
const bumpDrafts = () => { draftsTick.value++; };
const onDraftsStorage = e => { if (!e.key || e.key === 'fangate_drafts') bumpDrafts(); };
onMounted(() => {
  window.addEventListener('fangate-drafts-changed', bumpDrafts);
  window.addEventListener('storage', onDraftsStorage);
  window.addEventListener('focus', bumpDrafts);
});
onUnmounted(() => {
  window.removeEventListener('fangate-drafts-changed', bumpDrafts);
  window.removeEventListener('storage', onDraftsStorage);
  window.removeEventListener('focus', bumpDrafts);
});
const getDraft = convId => {
  // eslint-disable-next-line no-unused-expressions
  draftsTick.value;   // touch to make this reactive to the interval
  if (!convId) return '';
  try {
    const drafts = JSON.parse(localStorage.getItem('fangate_drafts') || '{}');
    return String(drafts[convId] || '').trim();
  } catch (_) { return ''; }
};

// Short preview of the conversation's last message — shown under the
// contact name in the chat list. Handles text + attachment shorthand so
// the row reads like Telegram / WhatsApp instead of just the contact type.
const lastMessagePreview = conv => {
  if (!conv) return '';
  let m = null;
  try {
    m = (conv.messages && conv.messages.length)
      ? getLastMessage(conv)
      : conv.last_non_activity_message;
  } catch (_) {}
  if (!m) return '';

  // Outgoing messages get a "You: " prefix so it's clear who said what.
  const prefix = m.message_type === 1 ? 'You: ' : '';

  // Text wins when present — a link with a Telegram unfurl arrives as text +
  // an auto-generated preview-thumbnail attachment, and we'd rather show the
  // link than a misleading "📷 Photo". Same for captioned media: show the words.
  const text = String(m.content || '').trim();
  if (text) {
    // Collapse newlines + clamp; CSS handles overflow ellipsis but a short
    // input avoids loading huge strings into the DOM.
    return prefix + text.replace(/\s+/g, ' ').slice(0, 120);
  }

  // No text → single-glyph type indicator for the attachment.
  const atts = Array.isArray(m.attachments) ? m.attachments : [];
  if (atts.length) {
    const t = String(atts[0].file_type || '').toLowerCase();
    if (t.includes('image'))    return prefix + '📷 Photo';
    if (t.includes('video'))    return prefix + '🎥 Video';
    if (t.includes('audio'))    return prefix + '🎵 Audio';
    if (t.includes('voice'))    return prefix + '🎤 Voice message';
    if (t.includes('location')) return prefix + '📍 Location';
    if (t.includes('file'))     return prefix + '📎 File';
    return prefix + '📎 Attachment';
  }

  return prefix + '…';
};

// Actions — local-state resolve (prototype approach: immediate feedback)
// Resolved section is NOT auto-opened; user expands it manually.
const ctxResolve = () => {
  if (!chatCtx.conv) return;
  const convId      = chatCtx.conv.id;
  const wasResolved = resolvedConvIds.has(convId);
  if (wasResolved) { resolvedConvIds.delete(convId); }
  else             { resolvedConvIds.add(convId); }
  // Fire-and-forget Chatwoot API call — UI already reflects the change
  ConversationAPI.toggleStatus({
    conversationId: convId,
    status: wasResolved ? 'open' : 'resolved',
  }).catch(() => {});
  closeChatCtx();
};

// Mark read / Mark unread — single context-menu item that flips based on
// the current unread state of the conversation.
//   • unread_count > 0 → "Mark as read"  → commits 0 + fire-and-forget API
//   • unread_count = 0 → "Mark as unread" → dispatches Vuex action
// In both branches we update Vuex immediately so the chat-list pill +
// sidebar pill re-render the same frame the menu closes.
const ctxToggleRead = async () => {
  if (!chatCtx.conv) return;
  const conv = chatCtx.conv;
  if ((conv.unread_count || 0) > 0) {
    // Mark as read — same instant-feedback path as conversation open.
    const nowSec = Math.floor(Date.now() / 1000);
    store.commit('UPDATE_MESSAGE_UNREAD_COUNT', {
      id: conv.id,
      lastSeen: nowSec,
      unreadCount: 0,
    });
    try { await ConversationAPI.markMessageRead({ id: conv.id }); }
    catch (_) {}
  } else {
    // Mark as unread — Chatwoot's action commits the mutation for us.
    try { await store.dispatch('markMessagesUnread', { id: conv.id }); }
    catch (_) {}
  }
  closeChatCtx();
};

// Mute / Unmute. SINGLE source of truth = conv.muted (backend-backed: the
// serializer ships `muted`, so it's correct cross-device and after reload).
// We dropped the old per-device localStorage overlay — it was only there to
// paper over the backend mute not updating conv.muted in the frontend, and it
// caused desync bugs (unmuting via one toggle didn't clear the other's state).
// Now both toggles flip conv.muted OPTIMISTICALLY (just that one property — never
// spread the whole conv, which used to clobber `status`). IMPORTANT: muting does
// NOT move the chat to the Blocked section — it stays in the list with just the
// speaker-off icon.
const isMutedLocal = conv => !!conv?.muted;

// Unread count shown ON A CONVERSATION ROW in the list. Muted conversations
// STILL show their count + highlight here — the user wants to see "N new
// messages" in the overview even when muted. Standard WhatsApp/Telegram
// behaviour: a muted chat still shows its unread count in the list.
const unreadOf = conv => (conv?.unread_count || 0);

// Unread count for AGGREGATE / NOTIFICATION purposes — section counters
// ("X Unread" badges), the "Unread" tab filter, unread sorting, and (via
// FangateLayout) the account/sidebar badges. Muted conversations contribute
// ZERO here so they don't inflate any counter or bubble to the top: muting
// silences the notification surfaces, only the per-conversation row (unreadOf)
// still shows the number.
const notifyUnreadOf = conv => (isMutedLocal(conv) ? 0 : (conv?.unread_count || 0));

const ctxToggleMute = async () => {
  if (!chatCtx.conv) return;
  const conv     = chatCtx.conv; // a reference to the Vuex conversation object
  const wasMuted = !!conv.muted;
  // Optimistically flip ONLY conv.muted (single source of truth). chatCtx.conv
  // is a reference to the Vuex object, so the chat-row icon + the chat-header +
  // the per-inbox unread badges all reflect it the same frame. We touch only
  // `muted` — never spread the whole conv, which used to clobber `status` and
  // make muted chats look resolved. Revert if the backend call fails.
  conv.muted = !wasMuted;
  try {
    if (wasMuted) await ConversationAPI.unmute(conv.id);
    else          await ConversationAPI.mute(conv.id);
  } catch (_) {
    conv.muted = wasMuted; // revert on failure
  }
  closeChatCtx();
};

// Block / Unblock the contact. Tracked purely via OUR localStorage set —
// we deliberately ignore conv.meta.sender.blocked from Vuex because:
//   (a) Chatwoot's contact-update endpoint doesn't always re-broadcast the
//       updated contact through the conversations.meta.sender path, so the
//       Vuex flag can lag for hours and we'd be "stuck" on stale state.
//   (b) Tying it ONLY to our set means muting a previously-blocked contact
//       (or any other unrelated action) can never accidentally re-mark the
//       row as blocked.
// Persisted locally so the BLOCKED section survives reloads. Backend still
// gets the PATCH so block state syncs cross-device once we move tokens
// server-side (#91).
const BLOCKED_KEY = 'fangate_blocked_convs';
const readBlockedSet = () => {
  try { return new Set(JSON.parse(localStorage.getItem(BLOCKED_KEY) || '[]')); }
  catch (_) { return new Set(); }
};
const writeBlockedSet = s => {
  try { localStorage.setItem(BLOCKED_KEY, JSON.stringify([...s])); } catch (_) {}
};
const blockedConvIds = reactive(readBlockedSet());
const isContactBlocked = conv => !!(conv && blockedConvIds.has(conv.id));

// Telegram's User object carries `is_bot`. We persist it on
// contact.additional_attributes.telegram_is_bot during incoming-message
// processing (Telegram::IncomingMessageService). When true, we hide the
// Block menu item — blocking another bot is almost never what the agent
// intends and the option just clutters the menu.
const isBotContact = conv =>
  !!(conv?.meta?.sender?.additional_attributes?.telegram_is_bot);

const blockedList = computed(() =>
  conversations.value.filter(c => isContactBlocked(c) && !resolvedConvIds.has(c.id))
);
const blockedSecOpen = ref(false);

const ctxToggleBlock = async () => {
  if (!chatCtx.conv) return;
  const conv      = chatCtx.conv;
  const contactId = conv.meta?.sender?.id;
  const wasBlocked = isContactBlocked(conv);
  // Optimistic local toggle so the row jumps section instantly.
  if (wasBlocked) blockedConvIds.delete(conv.id);
  else            blockedConvIds.add(conv.id);
  writeBlockedSet(blockedConvIds);
  // Fire-and-forget backend sync (only if we have a contact id to PATCH).
  if (contactId) {
    try {
      const ContactAPI = (await import('dashboard/api/contacts')).default;
      await ContactAPI.update(contactId, { blocked: !wasBlocked });
    } catch (_) {
      // Roll back the local toggle if the API call failed.
      if (wasBlocked) blockedConvIds.add(conv.id);
      else            blockedConvIds.delete(conv.id);
      writeBlockedSet(blockedConvIds);
    }
  }
  closeChatCtx();
};

// Delete the conversation entirely (admin-level destructive action). The
// row is removed from local state immediately; the backend DELETE fires
// fire-and-forget. If the API rejects (e.g. permissions), we just log —
// a hard refresh would resurrect the row.
const deletedConvIds = reactive(new Set());
const ctxDelete = async () => {
  if (!chatCtx.conv) return;
  const conv = chatCtx.conv;
  const ok = await confirm({
    title: 'Delete conversation?',
    message: `Delete conversation with ${conv.meta?.sender?.name || 'this contact'}? This cannot be undone.`,
    confirmLabel: 'Delete',
    danger: true,
  });
  if (!ok) {
    closeChatCtx();
    return;
  }
  deletedConvIds.add(conv.id);
  try {
    await ConversationAPI.delete(conv.id);
  } catch (e) {
    deletedConvIds.delete(conv.id);
    // eslint-disable-next-line no-console
    console.warn('[fangate] delete conversation failed', e?.message);
  }
  closeChatCtx();
};

// Row click = set/unset the PRIMARY assignee. Clicking the currently assigned
// agent unassigns (agentId 0). The conv's meta is updated optimistically so the
// ✓ state is correct the next time the menu opens (the list payload itself only
// refreshes via websocket/poll).
const ctxAssignAgent = async agent => {
  if (!chatCtx.conv || !agent) return;
  const isCurrent = chatCtx.conv.meta?.assignee?.id === agent.id;
  try {
    await ConversationAPI.assignAgent({
      conversationId: chatCtx.conv.id,
      agentId: isCurrent ? 0 : agent.id,
    });
    if (chatCtx.conv.meta) chatCtx.conv.meta.assignee = isCurrent ? null : agent;
  } catch (_) {}
  closeChatCtx();
};

// "+" toggle = add/remove this agent as an ADDITIONAL agent on the chat
// (conversation participant). PATCH replaces the whole set, so we send the
// updated list. Menu stays open so several agents can be added in one go.
const ctxToggleParticipant = async agent => {
  if (!chatCtx.conv || !agent) return;
  const has = chatCtx.participantIds.includes(agent.id);
  const next = has
    ? chatCtx.participantIds.filter(id => id !== agent.id)
    : [...chatCtx.participantIds, agent.id];
  try {
    await ConversationAPI.updateParticipants({ conversationId: chatCtx.conv.id, userIds: next });
    chatCtx.participantIds = next;
  } catch (_) { /* keep previous state on failure */ }
};

// Close chat ctx on outside click (in addition to existing closeDropdowns)
const closeChatCtxOnOutside = () => { if (chatCtx.show) closeChatCtx(); };

// Events
const selectInbox        = inbox => emit('select-inbox', inbox.id);
const selectConversation = conv  => emit('select-conversation', conv);
const goBack             = ()   => emit('back');

// ----- Inline label picker (anchored to the chat row "+" button) ------------
const convLabelPicker = reactive({
  show: false,
  x: 0, y: 0,
  conv: null,
});

const openConvLabelPicker = (e, conv) => {
  e.stopPropagation();
  const rect = e.currentTarget.getBoundingClientRect();
  convLabelPicker.x    = rect.left;
  convLabelPicker.y    = rect.bottom + 4;
  convLabelPicker.conv = conv;
  convLabelPicker.show = true;
};

// Right-click context menu → "Add label" entry. Uses the chat from chatCtx
// (the one that was right-clicked) and positions the picker next to the
// context menu so the user sees the labels appear instantly.
const ctxOpenLabelPicker = () => {
  if (!chatCtx.conv) { closeChatCtx(); return; }
  // Anchor the picker roughly where the context menu currently sits
  convLabelPicker.x    = chatCtx.x;
  convLabelPicker.y    = chatCtx.y;
  convLabelPicker.conv = chatCtx.conv;
  convLabelPicker.show = true;
  closeChatCtx();
};
const closeConvLabelPicker = () => {
  convLabelPicker.show = false;
  convLabelPicker.conv = null;
};
const toggleConvLabel = async lbl => {
  const conv = convLabelPicker.conv;
  if (!conv) return;
  const current = new Set(conv.labels || []);
  if (current.has(lbl.title)) current.delete(lbl.title);
  else current.add(lbl.title);
  const next = [...current];
  // Optimistic update on the local conversation object
  conv.labels = next;
  try {
    const { default: ConvAPI } = await import('dashboard/api/conversations');
    await ConvAPI.updateLabels(conv.id, next);
  } catch (_) { /* rollback omitted — keep UI optimistic */ }
};

// Close on outside click (reuses the existing closeDropdowns chain)
const closeConvLabelOnOutside = () => {
  if (convLabelPicker.show) closeConvLabelPicker();
};
onMounted(()  => document.addEventListener('click', closeConvLabelOnOutside));
onUnmounted(() => document.removeEventListener('click', closeConvLabelOnOutside));
</script>

<template>
  <div id="list-pane-inner" :class="slideClass">

    <!-- ==================================================================
         LAYER 0 — Account / Inbox overview
         ================================================================== -->
    <div class="list-layer" id="layer-accounts">

      <!-- ============================================================
           AGENTS VIEW (overrides account list when view === 'agents')
           ============================================================ -->
      <template v-if="view === 'agents'">
        <div class="lp-header">
          <div class="lp-header-row">
            <h2 class="lp-title">Agents</h2>
            <span class="lp-badge">{{ agents.length }} Agents</span>
          </div>
          <div class="lp-subtitle">Team members in this account</div>
        </div>
        <div class="lp-scroll">
          <div v-if="agents.length === 0" class="lp-empty">
            No agents found
          </div>
          <div v-for="agent in agents" :key="'ag-' + agent.id"
               class="account-row" role="button">
            <div class="lp-avatar">
              <img v-if="agent.avatar_url || agent.thumbnail"
                   :src="agent.avatar_url || agent.thumbnail"
                   :alt="agent.name"
                   style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
              <span v-else>{{ getInitials(agent.name) }}</span>
            </div>
            <div style="flex:1;min-width:0;">
              <div class="lp-acc-name">{{ agent.name }}</div>
              <div class="lp-acc-sub">{{ agent.email || agent.role || 'Agent' }}</div>
            </div>
            <span class="lp-online-dot"
                  :class="{ 'lp-online-dot--off': agent.availability_status !== 'online' }"></span>
          </div>
        </div>
      </template>

      <!-- ============================================================
           LABEL VIEW — cross-account: every readable chat carrying the
           selected label (fetched server-side, ACL-respected).
           ============================================================ -->
      <template v-else-if="view === 'label'">
        <div class="lp-header">
          <div class="lp-header-row">
            <h2 class="lp-title">{{ selectedLabel }}</h2>
            <span class="lp-badge">{{ filteredLabelConversations.length }}{{ labelHasMore ? '+' : '' }} Chats</span>
            <!-- Sort: Activity (default) / A–Z by contact name -->
            <div class="lp-label-sort">
              <button class="lp-label-sort-btn" :class="{ active: labelSort === 'activity' }"
                      title="Sort by recent activity" @click="labelSort = 'activity'">Activity</button>
              <button class="lp-label-sort-btn" :class="{ active: labelSort === 'alpha' }"
                      title="Sort alphabetically by name" @click="labelSort = 'alpha'">A–Z</button>
            </div>
          </div>
          <div class="lp-subtitle">Chats with this label · all accounts</div>
          <div v-if="selectedLabelDescription" class="lp-subtitle"
               style="font-style:italic;margin-top:3px;">
            Use for: {{ selectedLabelDescription }}
          </div>
        </div>

        <!-- Type tabs: filter the label's chats by kind -->
        <div class="lp-tabs-row lp-tabs-row--fill">
          <button class="lp-tab" :class="{ active: labelTypeTab === 'all' }"
                  @click="labelTypeTab = 'all'">All</button>
          <button class="lp-tab" :class="{ active: labelTypeTab === 'chats' }"
                  @click="labelTypeTab = 'chats'">Chats</button>
          <button class="lp-tab" :class="{ active: labelTypeTab === 'channels' }"
                  @click="labelTypeTab = 'channels'">Channels</button>
          <button class="lp-tab" :class="{ active: labelTypeTab === 'groups' }"
                  @click="labelTypeTab = 'groups'">Groups</button>
        </div>

        <div class="lp-scroll">
          <div v-if="labelConvsLoading && !labelConversations.length" class="lp-empty">Loading…</div>
          <div v-else-if="!filteredLabelConversations.length" class="lp-empty">
            {{ labelConversations.length ? 'Nothing in this tab' : 'No chats with this label' }}
          </div>
          <div
            v-for="conv in pagedLabelConversations" :key="'lblc-' + conv.id"
            class="account-row" role="button"
            :class="{ active: Number(selectedConversationId) === Number(conv.id) }"
            @click="selectConversation(conv)"
          >
            <div class="lp-avatar"><span>{{ getInitials(convName(conv)) }}</span></div>
            <div style="flex:1;min-width:0;">
              <div class="lp-acc-name">{{ convName(conv) }}</div>
              <div class="lp-acc-sub">Account: {{ inboxNameOf(conv) }}</div>
            </div>
            <span class="lp-type-pill" :class="'lp-type-pill--' + convTypeKey(conv)">{{ convType(conv) }}</span>
            <span v-if="unreadOf(conv)" class="pill-unread" style="margin-left:6px;">{{ unreadOf(conv) }}</span>
          </div>
          <button v-if="labelHasMoreToShow" class="lp-load-more"
                  :disabled="labelConvsLoading" @click="loadMoreLabelConvs">
            {{ labelConvsLoading ? 'Loading…' : 'Load more' }}
          </button>
        </div>
      </template>

      <!-- ============================================================
           LABELS OVERVIEW — directory of every label. Opened from the
           sidebar's "Labels" section header. Click a row to drill into that
           label's cross-account chats/channels/groups (view === 'label').
           ============================================================ -->
      <template v-else-if="view === 'labels-overview'">
        <div class="lp-header">
          <div class="lp-header-row">
            <h2 class="lp-title">Labels</h2>
            <span class="lp-badge">{{ labels.length }} Labels</span>
          </div>
          <div class="lp-subtitle">Pick a label to see its chats across all accounts</div>
          <button v-if="isAdmin" class="lp-manage-labels" type="button"
                  @click="$emit('select-view', 'settings-labels')">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
            Manage Labels
          </button>
          <div v-else class="lp-subtitle" style="margin-top:5px;opacity:0.85;">
            Only admins can add or edit labels.
          </div>
        </div>
        <div class="lp-scroll">
          <div v-if="!labels.length" class="lp-empty">No labels yet</div>
          <div
            v-for="lbl in pagedLabels" :key="'lblo-' + lbl.id"
            class="account-row" role="button"
            @click="$emit('select-label', lbl.title)"
          >
            <span class="lp-label-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
            <div style="flex:1;min-width:0;">
              <div class="lp-acc-name">{{ lbl.title }}</div>
              <div v-if="lbl.description" class="lp-acc-sub">Use for: {{ lbl.description }}</div>
            </div>
            <svg class="lp-row-chev" width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
          <button v-if="labelsHasMore" class="lp-load-more" @click="loadMoreLabels">Load more</button>
        </div>
      </template>

      <!-- ============================================================
           ACCOUNTS VIEW (default)
           ============================================================ -->
      <template v-else>
      <!-- Header -->
      <div class="lp-header">
        <div class="lp-header-row">
          <h2 class="lp-title">{{ layer0Title }}</h2>
          <div class="lp-header-right">
            <span class="lp-badge lp-badge--accent" v-if="mode === 'unread'">
              {{ totalUnreadAccs }} Accounts
            </span>
            <span class="lp-badge" v-else>
              {{ filteredInboxes.length }} Accounts
            </span>
            <!-- Channels & Groups: info "i" → what a bot can read + how to set up -->
            <button v-if="view === 'channels'"
                    class="lp-info-btn"
                    title="When can a bot read channels & groups?"
                    @click.stop="showChannelsInfo = true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="lp-subtitle">{{ layer0Subtitle }}</div>
      </div>

      <!-- Search + toolbar -->
      <div class="lp-toolbar-wrap">
        <!-- Search -->
        <div class="lp-search-wrap">
          <svg class="lp-search-icon" width="12" height="12" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input v-model="accSearch" class="lp-search-input" placeholder="Search accounts…" />
        </div>

        <!-- Sort button -->
        <div class="lp-btn-wrap" style="position:relative;">
          <button class="lp-icon-btn" title="Sort" @click.stop="accSortOpen = !accSortOpen"
                  :style="{ borderColor: accSort !== 'default' ? 'var(--accent)' : 'var(--border-soft)',
                            background: accSort !== 'default' ? 'var(--accent-tint)' : 'var(--bg-panel)' }">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 :stroke="accSort !== 'default' ? '#0f766e' : '#6e7570'"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="15" y2="12"/>
              <line x1="3" y1="18" x2="9" y2="18"/>
              <polyline points="17 15 21 18 17 21"/>
            </svg>
          </button>
          <div v-show="accSortOpen" class="lp-dropdown" @click.stop style="right:0;">
            <div class="lp-dropdown-head">Sort</div>
            <div v-for="opt in ACC_SORT_OPTIONS" :key="opt.id"
                 class="lp-dropdown-row" :class="{ active: accSort === opt.id }"
                 @click="accSort = opt.id; accSortOpen = false">
              <span class="lp-radio" :class="{ checked: accSort === opt.id }">
                <svg v-if="accSort === opt.id" width="6" height="6" viewBox="0 0 10 10" fill="white">
                  <circle cx="5" cy="5" r="3"/>
                </svg>
              </span>
              {{ opt.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Account list -->
      <div class="lp-scroll">
        <div v-if="filteredInboxes.length === 0" class="lp-empty">
          No accounts found
        </div>
        <div v-for="inbox in filteredInboxes" :key="inbox.id"
             class="account-row" role="button"
             :class="{ 'account-row--unread': inbox.unreadCount > 0 }"
             @click="selectInbox(inbox)"
             @contextmenu.prevent.stop="$emit('open-agent-picker', { event: $event, inbox })">
          <!-- Avatar — img if available, else initials -->
          <div class="lp-avatar">
            <img v-if="inbox.avatar_url"
                 :src="inbox.avatar_url"
                 :alt="inbox.name"
                 style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
            <span v-else>{{ getInitials(inbox.name) }}</span>
          </div>
          <!-- Meta -->
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;justify-content:space-between;">
              <div class="lp-acc-name">{{ inbox.name }}</div>
              <span v-if="inbox.unreadCount > 0" class="pill-unread">{{ inbox.unreadCount }}</span>
              <span v-else class="lp-acc-count">{{ inbox.totalChats }} Chats</span>
            </div>
            <div class="lp-acc-sub">{{ inbox.channel_type?.replace('Channel::', '') ?? 'Inbox' }}</div>
          </div>
          <!-- Chevron -->
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="#6e7570" stroke-width="2" style="flex-shrink:0;">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>
      </template><!-- /v-else accounts view -->
    </div>

    <!-- ==================================================================
         LAYER 1 — Conversation list for selected inbox
         ================================================================== -->
    <div class="list-layer" id="layer-chats">

      <!-- Header -->
      <div class="lp-header lp-header--chats">
        <button class="lp-back-btn" @click="goBack" title="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.5">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <div style="flex:1;min-width:0;">
          <div ref="titleRowRef" style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
            <h2 class="lp-title lp-title--persona" style="font-size:15px;">
              <img v-if="selectedInbox?.avatar_url" :src="selectedInbox.avatar_url"
                   :alt="selectedInbox?.name" class="lp-title-avatar" />
              <span ref="nameRef" class="lp-title-persona-name">{{ selectedInbox?.name ?? '—' }}</span>
            </h2>
            <div style="display:flex;gap:6px;align-items:center;flex-shrink:0;">
              <template v-if="view === 'chats'">
                <span class="lp-count" :title="totalChats + ' chats'">
                  <span class="lp-count-num">{{ totalChats }}</span>
                  <span class="lp-count-lbl">chats</span>
                </span>
                <span v-if="unreadChats > 0" class="lp-badge lp-badge--accent">{{ unreadChats }} Unread</span>
              </template>
              <template v-else-if="view === 'channels'">
                <span class="lp-count" :title="channelList.length + ' channels'">
                  <span class="lp-count-num">{{ channelList.length }}</span>
                  <span class="lp-count-lbl">channels</span>
                </span>
              </template>
            </div>
          </div>
          <div v-if="view === 'channels'" class="lp-subtitle" style="margin-top:2px;">
            Channels &amp; groups
          </div>
        </div>
      </div>

      <!-- ============================================================
           CHATS VIEW
           ============================================================ -->
      <template v-if="view === 'chats'">

      <!-- Search + New-chat -->
      <div class="lp-toolbar-wrap" style="padding-top:0;">
        <div class="lp-search-wrap" style="flex:1;">
          <svg class="lp-search-icon" width="12" height="12" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input v-model="chatSearch" class="lp-search-input" placeholder="Search chats…" />
        </div>
        <!-- Start a new chat on this inbox. Wires to the New-Conversation
             composer (#143) — currently emits 'new-chat' so the parent
             can decide whether to open a contact picker / show a "feature
             pending" notice depending on inbox channel type. -->
        <button class="lp-new-chat-btn"
                title="Start a new chat on this inbox"
                @click="$emit('new-chat', selectedInbox?.id)">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.4"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="12" y1="5"  x2="12" y2="19"/>
            <line x1="5"  y1="12" x2="19" y2="12"/>
          </svg>
          New
        </button>
      </div>

      <!-- Tabs + toolbar -->
      <div class="lp-tabs-row lp-tabs-row--toolbar">
        <!-- Tab buttons -->
        <button class="lp-tab" :class="{ active: chatTab === 'all' }"
                @click="chatTab = 'all'">All</button>
        <button class="lp-tab" :class="{ active: chatTab === 'unread' }"
                @click="chatTab = 'unread'">Unread</button>

        <div style="flex:1;"></div>

        <!-- Filter -->
        <div style="position:relative;">
          <button class="lp-icon-btn" title="Filter chats"
                  @click.stop="chatSortOpen = false; chatFilterOpen = !chatFilterOpen"
                  :style="{ borderColor: filterActive ? 'var(--accent)' : 'var(--border-soft)',
                             background: filterActive ? 'var(--accent-tint)' : 'var(--bg-panel)' }">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 :stroke="filterActive ? '#0f766e' : '#6e7570'"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span v-if="filterActive" class="lp-active-dot"></span>
          </button>
          <div v-if="chatFilterOpen" class="lp-dropdown" @click.stop style="right:0;min-width:210px;max-height:340px;overflow-y:auto;">
            <div style="display:flex;align-items:center;justify-content:space-between;padding:4px 8px 2px;">
              <div class="lp-dropdown-head" style="padding:0;">Filter</div>
              <button v-if="filterActive" class="lp-clear-btn" @click="clearAllFilters">Clear all</button>
            </div>
            <!-- Status + Activity -->
            <template v-for="[group, opts] in filterGroups" :key="group">
              <div class="lp-dropdown-head" style="padding:6px 8px 3px;font-size:9px;">{{ group }}</div>
              <div v-for="opt in opts" :key="opt.id"
                   class="lp-dropdown-row" :class="{ active: activeFilters.has(opt.id) }"
                   @click="toggleChatFilter(opt.id)">
                <span class="lp-check" :class="{ checked: activeFilters.has(opt.id) }">
                  <svg v-if="activeFilters.has(opt.id)" width="8" height="8" viewBox="0 0 12 12">
                    <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  </svg>
                </span>
                {{ opt.label }}
              </div>
            </template>
            <!-- Agent filter -->
            <template v-if="agents.length">
              <div class="lp-fl-divider"></div>
              <div class="lp-dropdown-head" style="padding:6px 8px 3px;font-size:9px;color:var(--accent-strong);">Agent</div>
              <div v-for="agent in agents" :key="'a-' + agent.id"
                   class="lp-dropdown-row" :class="{ active: activeFilters.has('agent:' + agent.id) }"
                   @click="toggleChatFilter('agent:' + agent.id)">
                <span class="lp-check" :class="{ checked: activeFilters.has('agent:' + agent.id) }">
                  <svg v-if="activeFilters.has('agent:' + agent.id)" width="8" height="8" viewBox="0 0 12 12">
                    <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  </svg>
                </span>
                👤 {{ agent.name }}
              </div>
            </template>
            <!-- Label filter -->
            <div class="lp-fl-divider"></div>
            <div class="lp-dropdown-head" style="padding:6px 8px 3px;font-size:9px;color:var(--accent-strong);">Labels</div>
            <div v-if="!labels.length" style="font-family:'Inter',sans-serif;font-size:11px;color:var(--ink-muted);padding:2px 10px 6px;">
              No labels yet
            </div>
            <div v-for="lbl in labels" :key="'l-' + lbl.id"
                 class="lp-dropdown-row" :class="{ active: activeFilters.has('label:' + lbl.title) }"
                 @click="toggleChatFilter('label:' + lbl.title)">
              <span class="lp-check" :class="{ checked: activeFilters.has('label:' + lbl.title) }">
                <svg v-if="activeFilters.has('label:' + lbl.title)" width="8" height="8" viewBox="0 0 12 12">
                  <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                </svg>
              </span>
              <span class="lp-lbl-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
              {{ lbl.title }}
            </div>
          </div>
        </div>

        <!-- Sort -->
        <div style="position:relative;">
          <button class="lp-icon-btn" title="Sort chats"
                  @click.stop="chatFilterOpen = false; chatSortOpen = !chatSortOpen"
                  :style="{ borderColor: chatSort !== 'default' ? 'var(--accent)' : 'var(--border-soft)',
                             background: chatSort !== 'default' ? 'var(--accent-tint)' : 'var(--bg-panel)' }">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 :stroke="chatSort !== 'default' ? '#0f766e' : '#6e7570'"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="15" y2="12"/>
              <line x1="3" y1="18" x2="9" y2="18"/>
              <polyline points="17 15 21 18 17 21"/>
            </svg>
            <span v-if="chatSort !== 'default'" class="lp-active-dot"></span>
          </button>
          <div v-show="chatSortOpen" class="lp-dropdown" @click.stop style="right:0;">
            <div class="lp-dropdown-head">Sort</div>
            <div v-for="opt in CHAT_SORT_OPTIONS" :key="opt.id"
                 class="lp-dropdown-row" :class="{ active: chatSort === opt.id }"
                 @click="chatSort = opt.id; chatSortOpen = false">
              <span class="lp-radio" :class="{ checked: chatSort === opt.id }">
                <svg v-if="chatSort === opt.id" width="6" height="6" viewBox="0 0 10 10" fill="white">
                  <circle cx="5" cy="5" r="3"/>
                </svg>
              </span>
              {{ opt.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Active filter chips -->
      <div v-if="filterActive" class="lp-filter-chips">
        <span v-for="chip in activeFilterChips" :key="chip.id"
              class="lp-filter-chip">
          {{ chip.label }}
          <button class="lp-chip-rm" @click.stop="toggleChatFilter(chip.id)">×</button>
        </span>
      </div>

      <!-- Conversation list -->
      <div class="lp-scroll">
        <div v-if="convsLoading" class="lp-empty">
          <span style="opacity:0.5;">Loading chats…</span>
        </div>
        <div v-else-if="filteredConversations.length === 0 && resolvedList.length === 0" class="lp-empty">
          No chats found
        </div>
        <div v-for="conv in pagedConversations" :key="conv.id"
             class="chat-row"
             :class="{
               active: selectedConversationId === conv.id,
               'chat-row--unread': unreadOf(conv) > 0,
             }"
             @click="selectConversation(conv)"
             @contextmenu.prevent.stop="openChatCtx($event, conv)">
          <!-- Avatar with online dot (always shown; gray when offline) -->
          <div class="lp-avatar-wrap">
            <div class="lp-avatar lp-avatar--sm">
              <img v-if="conv.meta?.sender?.thumbnail"
                   :src="conv.meta.sender.thumbnail"
                   :alt="conv.meta?.sender?.name"
                   style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
              <span v-else>{{ getInitials(conv.meta?.sender?.name) }}</span>
            </div>
            <span class="lp-online-dot"
                  :class="{ 'lp-online-dot--off': !isOnline(conv) }"></span>
          </div>
          <!-- Body -->
          <div class="chat-row-body">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:4px;">
              <!-- Name + inline label chips. Adding new labels happens via the
                   right-click context menu ("Add label") — no inline + pill. -->
              <div class="chat-row-name-wrap">
                <span class="chat-row-name">{{ conv.meta?.sender?.name ?? 'Unknown' }}</span>
                <!-- Mute indicator — speaker-with-slash glyph next to the name
                     when the user has muted this conversation. Visual only;
                     doesn't move the row anywhere. -->
                <svg v-if="isMutedLocal(conv)" class="chat-row-mute"
                     width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <line x1="23" y1="9" x2="17" y2="15"/>
                  <line x1="17" y1="9" x2="23" y2="15"/>
                </svg>
                <span v-for="lbl in (conv.labels || [])" :key="lbl"
                      class="chat-row-lbl">{{ lbl }}</span>
              </div>
              <div class="chat-row-time-wrap">
                <span class="chat-row-time">{{ formatTime(conv.last_activity_at ?? conv.created_at) }}</span>
              </div>
            </div>
            <div class="chat-row-preview">
              <span class="chat-row-preview-text">
                <template v-if="getDraft(conv.id)">
                  <span class="chat-row-draft">Draft:</span>
                  {{ getDraft(conv.id).slice(0, 100) }}
                </template>
                <template v-else>{{ lastMessagePreview(conv) }}</template>
              </span>
              <!-- Delivery ticks sit on the preview line (right edge), aligned
                   with the last message instead of crowding the timestamp. -->
              <span v-if="statusTick(conv) === 'read'" class="lp-ticks lp-ticks--read">✓✓</span>
              <span v-else-if="statusTick(conv) === 'delivered'" class="lp-ticks lp-ticks--delivered">✓✓</span>
              <span v-else-if="statusTick(conv) === 'sent'" class="lp-ticks lp-ticks--sent">✓</span>
            </div>
          </div>
          <!-- Unread badge -->
          <div class="chat-row-meta" v-if="unreadOf(conv) > 0">
            <span class="pill-unread">{{ unreadOf(conv) }}</span>
          </div>
        </div>
        <button v-if="chatsHasMore" class="lp-load-more" @click="loadMoreChats">Load more</button>
        <button v-if="!chatsHasMore && isMtprotoListInbox" class="lp-load-more"
                :disabled="loadingTgDialogs" @click="loadOlderTelegramChats">
          {{ loadingTgDialogs ? 'Loading…' : 'Load older chats from Telegram' }}
        </button>

        <!-- Resolved section — ALWAYS visible header (matches prototype) -->
        <div class="lp-resolved-sec">
          <div class="lp-resolved-toggle" @click.stop="resolvedSecOpen = !resolvedSecOpen">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="var(--accent-strong)" stroke-width="2.5"
                 stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span class="lp-resolved-title">Resolved</span>
            <span class="lp-resolved-count">{{ resolvedList.length }}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="var(--ink-muted)" stroke-width="2.5"
                 class="lp-resolved-chev"
                 :style="{ transform: resolvedSecOpen ? 'none' : 'rotate(-90deg)' }">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </div>
          <div v-if="resolvedSecOpen">
            <div v-if="resolvedList.length === 0" class="lp-resolved-empty">
              No resolved chats
            </div>
            <div v-for="conv in resolvedList" :key="'r-' + conv.id"
                 class="chat-row lp-chat-resolved"
                 :class="{ active: selectedConversationId === conv.id }"
                 @click="selectConversation(conv)"
                 @contextmenu.prevent.stop="openChatCtx($event, conv)">
              <div class="lp-avatar-wrap">
                <div class="lp-avatar lp-avatar--sm" style="opacity:0.7;">
                  <img v-if="conv.meta?.sender?.thumbnail"
                       :src="conv.meta.sender.thumbnail"
                       :alt="conv.meta?.sender?.name"
                       style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
                  <span v-else>{{ getInitials(conv.meta?.sender?.name) }}</span>
                </div>
                <span class="lp-online-dot"
                      :class="{ 'lp-online-dot--off': !isOnline(conv) }"></span>
              </div>
              <div class="chat-row-body">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:4px;">
                  <div class="chat-row-name" style="opacity:0.7;">{{ conv.meta?.sender?.name ?? 'Unknown' }}</div>
                  <span class="chat-row-time">{{ formatTime(conv.last_activity_at ?? conv.created_at) }}</span>
                </div>
                <div class="chat-row-preview">{{ lastMessagePreview(conv) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Blocked section — same collapsible style as Resolved. Shows
             conversations whose contact has been blocked via the
             right-click "Block" action. -->
        <div class="lp-resolved-sec">
          <div class="lp-resolved-toggle" @click.stop="blockedSecOpen = !blockedSecOpen">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="#b91c1c" stroke-width="2.5"
                 stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
            <span class="lp-resolved-title">Blocked</span>
            <span class="lp-resolved-count">{{ blockedList.length }}</span>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                 stroke="var(--ink-muted)" stroke-width="2.5"
                 class="lp-resolved-chev"
                 :style="{ transform: blockedSecOpen ? 'none' : 'rotate(-90deg)' }">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </div>
          <div v-if="blockedSecOpen">
            <div v-if="blockedList.length === 0" class="lp-resolved-empty">
              No blocked contacts
            </div>
            <div v-for="conv in blockedList" :key="'b-' + conv.id"
                 class="chat-row lp-chat-resolved"
                 :class="{ active: selectedConversationId === conv.id }"
                 @click="selectConversation(conv)"
                 @contextmenu.prevent.stop="openChatCtx($event, conv)">
              <div class="lp-avatar-wrap">
                <div class="lp-avatar lp-avatar--sm" style="opacity:0.7;">
                  <img v-if="conv.meta?.sender?.thumbnail"
                       :src="conv.meta.sender.thumbnail"
                       :alt="conv.meta?.sender?.name"
                       style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
                  <span v-else>{{ getInitials(conv.meta?.sender?.name) }}</span>
                </div>
              </div>
              <div class="chat-row-body">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:4px;">
                  <div class="chat-row-name" style="opacity:0.7;">{{ conv.meta?.sender?.name ?? 'Unknown' }}</div>
                  <span class="chat-row-time">{{ formatTime(conv.last_activity_at ?? conv.created_at) }}</span>
                </div>
                <div class="chat-row-preview">{{ lastMessagePreview(conv) }}</div>
              </div>
            </div>
          </div>
        </div>

      </div>
      </template><!-- /v-if view === 'chats' -->

      <!-- ============================================================
           CHANNELS VIEW
           ============================================================ -->
      <template v-else-if="view === 'channels'">

      <!-- Search -->
      <div class="lp-toolbar-wrap" style="padding-top:0;">
        <div class="lp-search-wrap" style="flex:1;">
          <svg class="lp-search-icon" width="12" height="12" viewBox="0 0 24 24"
               fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input v-model="channelSearch" class="lp-search-input" placeholder="Search channels…" />
        </div>
      </div>

      <!-- Type tabs (All / Channels / Groups) + filter & sort.
           This filter/sort is INDEPENDENT of the chats list's. -->
      <div class="lp-tabs-row lp-tabs-row--toolbar">
        <button class="lp-tab" :class="{ active: channelTypeTab === 'all' }"
                @click="channelTypeTab = 'all'">All</button>
        <button class="lp-tab" :class="{ active: channelTypeTab === 'channels' }"
                @click="channelTypeTab = 'channels'">Channels</button>
        <button class="lp-tab" :class="{ active: channelTypeTab === 'groups' }"
                @click="channelTypeTab = 'groups'">Groups</button>

        <div style="flex:1;"></div>

        <!-- Filter (channels-only state) -->
        <div style="position:relative;">
          <button class="lp-icon-btn" title="Filter channels & groups"
                  @click.stop="chSortOpen = false; chFilterOpen = !chFilterOpen"
                  :style="{ borderColor: chFilterActive ? 'var(--accent)' : 'var(--border-soft)',
                             background: chFilterActive ? 'var(--accent-tint)' : 'var(--bg-panel)' }">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 :stroke="chFilterActive ? '#0f766e' : '#6e7570'"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            <span v-if="chFilterActive" class="lp-active-dot"></span>
          </button>
          <div v-if="chFilterOpen" class="lp-dropdown" @click.stop style="right:0;min-width:210px;max-height:340px;overflow-y:auto;">
            <div style="display:flex;align-items:center;justify-content:space-between;padding:4px 8px 2px;">
              <div class="lp-dropdown-head" style="padding:0;">Filter</div>
              <button v-if="chFilterActive" class="lp-clear-btn" @click="clearChFilters">Clear all</button>
            </div>
            <template v-for="[group, opts] in filterGroups" :key="'chg-' + group">
              <div class="lp-dropdown-head" style="padding:6px 8px 3px;font-size:9px;">{{ group }}</div>
              <div v-for="opt in opts" :key="'chg-' + opt.id"
                   class="lp-dropdown-row" :class="{ active: chActiveFilters.has(opt.id) }"
                   @click="toggleChFilter(opt.id)">
                <span class="lp-check" :class="{ checked: chActiveFilters.has(opt.id) }">
                  <svg v-if="chActiveFilters.has(opt.id)" width="8" height="8" viewBox="0 0 12 12">
                    <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  </svg>
                </span>
                {{ opt.label }}
              </div>
            </template>
            <template v-if="agents.length">
              <div class="lp-fl-divider"></div>
              <div class="lp-dropdown-head" style="padding:6px 8px 3px;font-size:9px;color:var(--accent-strong);">Agent</div>
              <div v-for="agent in agents" :key="'cha-' + agent.id"
                   class="lp-dropdown-row" :class="{ active: chActiveFilters.has('agent:' + agent.id) }"
                   @click="toggleChFilter('agent:' + agent.id)">
                <span class="lp-check" :class="{ checked: chActiveFilters.has('agent:' + agent.id) }">
                  <svg v-if="chActiveFilters.has('agent:' + agent.id)" width="8" height="8" viewBox="0 0 12 12">
                    <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                  </svg>
                </span>
                👤 {{ agent.name }}
              </div>
            </template>
            <div class="lp-fl-divider"></div>
            <div class="lp-dropdown-head" style="padding:6px 8px 3px;font-size:9px;color:var(--accent-strong);">Labels</div>
            <div v-if="!labels.length" style="font-family:'Inter',sans-serif;font-size:11px;color:var(--ink-muted);padding:2px 10px 6px;">
              No labels yet
            </div>
            <div v-for="lbl in labels" :key="'chl-' + lbl.id"
                 class="lp-dropdown-row" :class="{ active: chActiveFilters.has('label:' + lbl.title) }"
                 @click="toggleChFilter('label:' + lbl.title)">
              <span class="lp-check" :class="{ checked: chActiveFilters.has('label:' + lbl.title) }">
                <svg v-if="chActiveFilters.has('label:' + lbl.title)" width="8" height="8" viewBox="0 0 12 12">
                  <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round"/>
                </svg>
              </span>
              <span class="lp-lbl-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
              {{ lbl.title }}
            </div>
          </div>
        </div>

        <!-- Sort (channels-only state) -->
        <div style="position:relative;">
          <button class="lp-icon-btn" title="Sort channels & groups"
                  @click.stop="chFilterOpen = false; chSortOpen = !chSortOpen"
                  :style="{ borderColor: chSort !== 'default' ? 'var(--accent)' : 'var(--border-soft)',
                             background: chSort !== 'default' ? 'var(--accent-tint)' : 'var(--bg-panel)' }">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 :stroke="chSort !== 'default' ? '#0f766e' : '#6e7570'"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="15" y2="12"/>
              <line x1="3" y1="18" x2="9" y2="18"/>
              <polyline points="17 15 21 18 17 21"/>
            </svg>
            <span v-if="chSort !== 'default'" class="lp-active-dot"></span>
          </button>
          <div v-show="chSortOpen" class="lp-dropdown" @click.stop style="right:0;">
            <div class="lp-dropdown-head">Sort</div>
            <div v-for="opt in CHAT_SORT_OPTIONS" :key="'chs-' + opt.id"
                 class="lp-dropdown-row" :class="{ active: chSort === opt.id }"
                 @click="chSort = opt.id; chSortOpen = false">
              <span class="lp-radio" :class="{ checked: chSort === opt.id }">
                <svg v-if="chSort === opt.id" width="6" height="6" viewBox="0 0 10 10" fill="white">
                  <circle cx="5" cy="5" r="3"/>
                </svg>
              </span>
              {{ opt.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Channel list -->
      <div class="lp-scroll">
        <!-- Empty state — bot has no channels linked yet -->
        <div v-if="displayedChannels.length === 0" class="lp-channels-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.5"
               stroke-linecap="round" stroke-linejoin="round"
               class="lp-channels-empty-icon">
            <path d="M3 11l18-8-8 18-2-7-8-3z"/>
          </svg>
          <div class="lp-channels-empty-title">
            <template v-if="channelList.length">No channels found</template>
            <template v-else>No channels yet</template>
          </div>
          <div class="lp-channels-empty-sub">
            <template v-if="channelList.length">
              No channels or groups match the current tab / filter / search.
            </template>
            <template v-else>
              {{ selectedInbox?.name ?? 'This account' }} has no linked Telegram
              channels or groups yet. Add this account's bot as a member (for
              groups) or admin (for broadcast channels) on Telegram — new
              messages there will appear here automatically.
            </template>
          </div>
        </div>
        <!-- Channel/group rows — each is a real conversation, so clicking
             opens it in the chat area exactly like a private DM. -->
        <div v-for="conv in pagedChannels" :key="'ch-' + conv.id"
             class="chat-row"
             :class="{
               active: selectedConversationId === conv.id,
               'chat-row--unread': unreadOf(conv) > 0,
             }"
             @click="selectConversation(conv)"
             @contextmenu.prevent.stop="openChatCtx($event, conv)">
          <div class="lp-avatar-wrap">
            <div class="lp-avatar lp-avatar--sm lp-channel-icon">
              <!-- Real group/channel photo when we have one (same source as the
                   chat rows + New-Chat picker); else fall back to the type icon. -->
              <img v-if="conv.meta?.sender?.thumbnail"
                   :src="conv.meta.sender.thumbnail"
                   :alt="conv.meta?.sender?.name"
                   style="width:100%;height:100%;border-radius:50%;object-fit:cover;" />
              <template v-else>
                <!-- Broadcast-channel megaphone vs group people icon -->
                <svg v-if="conv.additional_attributes?.chat_type === 'channel'"
                     width="17" height="17" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 11l18-8-8 18-2-7-8-3z"/>
                </svg>
                <svg v-else
                     width="17" height="17" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </template>
            </div>
          </div>
          <div class="chat-row-body">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:4px;">
              <div style="display:flex;align-items:center;gap:5px;min-width:0;">
                <div class="chat-row-name">{{ conv.meta?.sender?.name ?? 'Channel' }}</div>
                <!-- Mute indicator — same speaker-with-slash glyph as the CHATS
                     rows, shown when this channel/group is muted. -->
                <svg v-if="isMutedLocal(conv)" class="chat-row-mute"
                     width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2.2"
                     stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <line x1="23" y1="9" x2="17" y2="15"/>
                  <line x1="17" y1="9" x2="23" y2="15"/>
                </svg>
              </div>
              <span class="chat-row-time">{{ formatTime(conv.last_activity_at ?? conv.created_at) }}</span>
            </div>
            <div class="chat-row-preview chat-row-preview--clamp2">{{ lastMessagePreview(conv) }}</div>
          </div>
          <!-- Right column: unread pill on top, GROUP/CHANNEL type chip below.
               Moving the type chip out of the name row gives the title back
               its full horizontal space (was truncating to "Testg…"). -->
          <div class="chat-row-meta chat-row-meta--stack">
            <span v-if="unreadOf(conv) > 0" class="pill-unread">{{ unreadOf(conv) }}</span>
            <span class="lp-ch-type"
                  :class="conv.additional_attributes?.chat_type === 'channel'
                          ? 'lp-ch-type--channel' : 'lp-ch-type--group'">
              {{ conv.additional_attributes?.chat_type === 'channel' ? 'Channel' : 'Group' }}
            </span>
          </div>
        </div>
        <button v-if="channelsHasMore" class="lp-load-more" @click="loadMoreChannels">Load more</button>
        <button v-if="!channelsHasMore && isMtprotoListInbox" class="lp-load-more"
                :disabled="loadingTgDialogs" @click="loadOlderTelegramChats">
          {{ loadingTgDialogs ? 'Loading…' : 'Load older chats from Telegram' }}
        </button>
      </div>
      </template><!-- /v-else-if view === 'channels' -->

    </div>

  </div><!-- /#list-pane-inner -->

  <!-- ================================================================
       CHANNELS & GROUPS — bot capability info panel
       ================================================================ -->
  <div v-if="showChannelsInfo" class="lp-info-overlay" @click.self="showChannelsInfo = false">
    <div class="lp-info-card" @click.stop>
      <div class="lp-info-head">
        <h3 class="lp-info-title">Bots in Channels &amp; Groups</h3>
        <button class="lp-info-close" title="Close" @click="showChannelsInfo = false">×</button>
      </div>

      <p class="lp-info-intro">
        Whether channel &amp; group messages appear here depends on the bot's
        permissions inside Telegram. Here's what works — and how to set it up.
      </p>

      <div class="lp-info-section">
        <div class="lp-info-h">Groups &amp; supergroups</div>
        <ul class="lp-info-ul">
          <li>By default a bot only sees messages that <strong>mention it</strong>,
              <strong>reply to it</strong>, or are <strong>commands</strong> — not every message.</li>
          <li>To read <strong>all</strong> group messages, do one of:
            <ul>
              <li>make the bot an <strong>admin</strong> of the group, <em>or</em></li>
              <li>disable <strong>Group Privacy</strong> in
                  <a href="https://t.me/BotFather" target="_blank" rel="noopener">@BotFather</a>
                  (<code>/setprivacy</code> → <em>Disable</em>).</li>
            </ul>
          </li>
        </ul>
      </div>

      <div class="lp-info-section">
        <div class="lp-info-h">Channels (broadcast)</div>
        <ul class="lp-info-ul">
          <li>The bot must be an <strong>admin</strong> of the channel — then channel
              posts show up here.</li>
          <li>Channels are broadcast-only: there are no subscriber messages to read,
              only what admins post.</li>
        </ul>
      </div>

      <div class="lp-info-section">
        <div class="lp-info-h">Set it up</div>
        <ol class="lp-info-ol">
          <li>Add this account's bot to the group or channel.</li>
          <li><strong>Channels:</strong> promote the bot to admin (required).<br>
              <strong>Groups:</strong> promote to admin (recommended) <em>or</em>
              turn off privacy mode in @BotFather.</li>
          <li>Reload the account here — the channels &amp; groups will appear.</li>
        </ol>
      </div>

      <p class="lp-info-note">
        Note: a brand-new bot can't see messages sent <em>before</em> it joined.
      </p>
    </div>
  </div>

  <!-- ================================================================
       INLINE LABEL PICKER (position:fixed, anchored to chat-row "+" button)
       ================================================================ -->
  <div v-if="convLabelPicker.show"
       class="lp-label-picker"
       :style="{ left: convLabelPicker.x + 'px', top: convLabelPicker.y + 'px' }"
       @click.stop>
    <div class="lp-label-picker-head">
      <span>Labels for this chat</span>
      <button class="lp-label-picker-close" @click="closeConvLabelPicker">×</button>
    </div>
    <div v-if="!labels.length" class="lp-label-empty">
      No labels created yet.<br>
      Go to <strong>Settings → Labels</strong> to create your first one.
    </div>
    <button v-for="lbl in labels" :key="lbl.id"
            class="lp-label-row"
            :class="{ active: (convLabelPicker.conv?.labels || []).includes(lbl.title) }"
            @click="toggleConvLabel(lbl)">
      <span class="lp-label-check"
            :class="{ checked: (convLabelPicker.conv?.labels || []).includes(lbl.title) }">
        <svg v-if="(convLabelPicker.conv?.labels || []).includes(lbl.title)"
             width="8" height="8" viewBox="0 0 12 12">
          <polyline points="2 6 5 9 10 3" stroke="white" stroke-width="1.5"
                    fill="none" stroke-linecap="round"/>
        </svg>
      </span>
      <span class="lp-label-color-dot" :style="{ background: lbl.color || '#0d9488' }"></span>
      <span>{{ lbl.title }}</span>
    </button>
  </div>

  <!-- ================================================================
       CHAT CONTEXT MENU (position:fixed, shown on right-click of chat row)
       ================================================================ -->
  <div
    v-if="chatCtx.show"
    class="lp-ctx-wrap"
    :style="{ left: chatCtx.x + 'px', top: chatCtx.y + 'px' }"
    @click.stop
  >
    <!-- Resolve / Unresolve -->
    <button
      class="ctx-item"
      :style="isResolved(chatCtx.conv) ? '' : 'color:var(--accent-strong)'"
      @click="ctxResolve"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
      {{ isResolvedConv(chatCtx.conv) ? 'Mark as unresolved' : 'Mark as resolved' }}
    </button>
    <!-- Assign to — ADMINS ONLY. Agents must not be able to (re)assign a chat or
         channel to other agents (security: keeps agents scoped to their own
         work). The popover-based picker is already admin-gated in FangateLayout;
         this gates the context-menu path too, for both chats and channels. -->
    <template v-if="isAdmin">
    <div class="ctx-sep"></div>

    <!-- Assign to (with agent submenu) -->
    <button
      class="ctx-item"
      @click.stop="ctxToggleAgentSub"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
      Assign to
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" style="margin-left:auto;">
        <path d="m9 18 6-6-6-6"/>
      </svg>
    </button>
    <!-- Inline agent submenu -->
    <div v-if="chatCtx.agentSubShow" class="lp-ctx-sub">
      <div class="lp-ctx-sub-head">Assign agent</div>
      <button v-for="agent in assignableAgents" :key="agent.id"
              class="ctx-item lp-ctx-agent-row"
              :class="{ 'lp-ctx-agent-row--assigned': chatCtx.conv?.meta?.assignee?.id === agent.id }"
              :title="chatCtx.conv?.meta?.assignee?.id === agent.id
                        ? 'Assigned — click to unassign'
                        : 'Click to assign this chat'"
              @click="ctxAssignAgent(agent)">
        <span class="lp-ctx-agent-check">
          {{ chatCtx.conv?.meta?.assignee?.id === agent.id ? '✓' : '' }}
        </span>
        <span class="lp-ctx-agent-name">{{ agent.name }}</span>
        <!-- "+" = also add this agent to the chat (participant), without
             changing the primary assignee. Stays open for multiple adds. -->
        <span class="lp-ctx-part-btn"
              :class="{ 'lp-ctx-part-btn--on': chatCtx.participantIds.includes(agent.id) }"
              :title="chatCtx.participantIds.includes(agent.id)
                        ? 'Added to this chat — click to remove'
                        : 'Add as additional agent'"
              @click.stop="ctxToggleParticipant(agent)">
          {{ chatCtx.participantIds.includes(agent.id) ? '✓' : '+' }}
        </span>
      </button>
      <div v-if="assignableAgents.length" class="lp-ctx-sub-hint">
        Click a name to assign · use <strong>+</strong> to add more agents
      </div>
      <div v-if="!assignableAgents.length"
           style="padding:4px 12px;font-family:'Inter',sans-serif;font-size:11px;color:var(--ink-muted);">
        No agents yet — invite agents under Settings → Agents.
      </div>
    </div>
    </template>
    <div class="ctx-sep"></div>

    <!-- Add label — opens inline label picker anchored to the context menu -->
    <button class="ctx-item" @click="ctxOpenLabelPicker">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
        <line x1="7" y1="7" x2="7.01" y2="7"/>
      </svg>
      Add label
    </button>
    <div class="ctx-sep"></div>

    <!-- Mark as read / unread — label flips based on current state -->
    <button class="ctx-item" @click="ctxToggleRead">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      {{ (chatCtx.conv?.unread_count || 0) > 0 ? 'Mark as read' : 'Mark as unread' }}
    </button>

    <!-- Mute / Unmute -->
    <button class="ctx-item" @click="ctxToggleMute">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
        <template v-if="isMuted(chatCtx.conv)">
          <line x1="23" y1="9" x2="17" y2="15"/>
          <line x1="17" y1="9" x2="23" y2="15"/>
        </template>
        <template v-else>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
        </template>
      </svg>
      {{ isMutedLocal(chatCtx.conv) ? 'Unmute' : 'Mute' }}
    </button>
    <div class="ctx-sep"></div>

    <!-- Block / Unblock — flips contact.blocked on the backend. Hidden
         for bot contacts since blocking another bot rarely makes sense
         (and the action wouldn't drop their messages — bots don't get
         caught by our incoming-message filter the same way users do). -->
    <button v-if="!isBotContact(chatCtx.conv)"
            class="ctx-item danger" @click="ctxToggleBlock">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
      </svg>
      {{ isContactBlocked(chatCtx.conv) ? 'Unblock' : 'Block' }}
    </button>

    <!-- Delete — destroys the conversation (confirm first). -->
    <button class="ctx-item danger" @click="ctxDelete">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6"/><path d="M14 11v6"/>
        <path d="M9 6V4h6v2"/>
      </svg>
      Delete
    </button>
  </div>

</template>

<style lang="scss" scoped>
// ---- Header -----------------------------------------------------------------
.lp-header {
  padding: 16px 20px 8px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-soft);
}
.lp-header--chats {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 14px 14px 10px;
}
.lp-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
// Right-aligned cluster: count badge + (channels) info button, kept together.
.lp-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.lp-info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px; height: 22px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
  &:hover {
    background: var(--accent-tint);
    color: var(--accent-strong);
    border-color: var(--accent-ring);
  }
}

// ---- Channels & Groups info panel (modal) ---------------------------------
.lp-info-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(31, 42, 42, 0.34);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.lp-info-card {
  width: 100%;
  max-width: 440px;
  max-height: 84vh;
  overflow-y: auto;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(31, 42, 42, 0.22);
  padding: 20px 22px 22px;
}
.lp-info-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.lp-info-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 17px;
  font-weight: 700;
  color: var(--ink-strong);
  margin: 0;
}
.lp-info-close {
  border: none;
  background: transparent;
  color: var(--ink-muted);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  &:hover { color: var(--ink-strong); }
}
.lp-info-intro {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ink-muted);
  margin: 0 0 14px;
}
.lp-info-section { margin-bottom: 14px; }
.lp-info-h {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent-strong);
  margin-bottom: 6px;
}
.lp-info-ul, .lp-info-ol {
  margin: 0;
  padding-left: 18px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ink-strong);
  ul { padding-left: 16px; margin-top: 3px; }
  li { margin-bottom: 4px; }
  code {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 12px;
    background: var(--bg-subtle);
    padding: 1px 5px;
    border-radius: 4px;
  }
  a { color: var(--accent-strong); text-decoration: underline; }
}
.lp-info-note {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ink-muted);
  margin: 4px 0 0;
  padding-top: 10px;
  border-top: 1px dashed var(--border-soft);
}
.lp-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 17px;
  font-weight: 700;
  color: var(--ink-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
// Persona-variant title: [TG icon] of <name>. Kept compact so long creator
// names don't truncate the way "Henning's Telegram chats" did.
.lp-title--persona {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
}
// Stacked chat count: number centred over the word, tucked to the right edge.
.lp-count {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.05;
  flex-shrink: 0;
}
.lp-count-num {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-strong);
}
.lp-count-lbl {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px;
  font-weight: 600;
  color: var(--ink-muted);
}
.lp-title-tg {
  width: 14px; height: 14px;
  flex-shrink: 0;
  display: block;
}
.lp-title-avatar {
  width: 30px; height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.lp-title-of {
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
}
.lp-title-persona-name {
  // Corporate Creator-Chat brand teal — sets the persona name apart from
  // the muted "of" connector and ties back to the sidebar / active states.
  color: var(--accent-strong);
  font-weight: 800;
  // Title font for the name stays Libre Baskerville (inherits from
  // .lp-title) so the persona reads as the proper-noun anchor of the row.
  // flex:1 + min-width:0 lets it fill the title bar and truncate; fitName()
  // (JS) shrinks the font to keep long names readable before the ellipsis.
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lp-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 2px;
}
.lp-badge {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-subtle);
  color: var(--ink-muted);
  flex-shrink: 0;
  &--accent {
    background: var(--accent-soft);
    color: var(--accent-strong);
  }
}

// ---- Toolbar ----------------------------------------------------------------
.lp-toolbar-wrap {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px 6px;
  flex-shrink: 0;
}
// "Manage Labels" CTA in the Labels overview (admins) → opens settings-labels.
// Lives here so it's reachable on mobile (the rail Labels icon lands on this
// overview; desktop also gets it, which is fine).
.lp-manage-labels {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 10px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid var(--accent-ring);
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover { background: var(--accent-strong); color: #fff; border-color: var(--accent-strong); }
  svg { flex-shrink: 0; }
}
.lp-search-wrap {
  position: relative;
  flex: 1;
}
.lp-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ink-muted);
  pointer-events: none;
}
.lp-search-input {
  width: 100%;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  // Modern pill — rounded, borderless, soft shadow (desktop + mobile).
  padding: 8px 13px 8px 31px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: var(--bg-panel);
  box-shadow: 0 1px 2px rgba(20, 22, 40, 0.05);
  outline: none;
  color: var(--ink-strong);
  &::placeholder { color: var(--ink-muted); }
  &:focus { border-color: var(--accent-ring); }
}

// "New" pill sits next to the search input — same height, corporate teal,
// tiny + glyph + "New" label. Triggers the new-chat composer wiring (#143).
.lp-new-chat-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 11px;
  border-radius: 9px;
  border: 1px solid var(--accent-ring);
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  &:hover {
    background: var(--accent-strong);
    color: #fff;
    border-color: var(--accent-strong);
  }
  svg { flex-shrink: 0; }
}

// ---- Icon buttons -----------------------------------------------------------
.lp-btn-wrap { position: relative; }
.lp-icon-btn {
  position: relative;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0 !important;
  border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  cursor: pointer;
  transition: all 0.12s;
  flex-shrink: 0;
  &:hover { border-color: var(--border-medium); }
}
// Small teal dot in the top-right corner of a filter/sort icon button to
// signal "this control has an active selection" at a glance (the border tint
// alone is easy to miss).
.lp-active-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  border: 1.5px solid var(--bg-panel);
  pointer-events: none;
}

// ---- Tabs -------------------------------------------------------------------
.lp-tabs-row {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px 8px;
  flex-shrink: 0;
}
// Fill variant — for rows that are ONLY tabs (e.g. the 4 label type tabs, no
// filter/sort icons): each tab shares the row equally and shrinks so nothing is
// clipped when the list pane is narrowed. Truncates with … only at extreme widths.
.lp-tabs-row--fill .lp-tab {
  flex: 1 1 0;
  min-width: 0;
  padding: 5px 4px;
  font-size: 10px;
  letter-spacing: 0.02em;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
// Toolbar variant — rows with tabs + filter/sort icons (chats, channels): the
// tabs may shrink (with …) so the fixed-size icons stay visible on a narrow pane.
.lp-tabs-row--toolbar .lp-tab {
  flex: 0 1 auto;
  min-width: 0;
  padding: 5px 9px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lp-tab {
  flex: 0 0 auto;
  padding: 5px 14px;
  // Segmented-control style — active = raised white pill; inactive = bare.
  // Desktop + mobile (the --fill/--toolbar variants still set size/flex).
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0;
  cursor: pointer;
  transition: all 0.12s;
  &.active {
    background: var(--bg-panel);
    color: var(--accent-strong);
    border-color: transparent;
    box-shadow: 0 1px 3px rgba(20, 22, 40, 0.10);
  }
}

// ---- Back button ------------------------------------------------------------
.lp-back-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0 !important;
  border-radius: 7px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
  &:hover { color: var(--ink-strong); border-color: var(--border-medium); }
}

// ---- Avatar -----------------------------------------------------------------
.lp-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--accent-tint);
  border: 1.5px solid var(--accent-ring);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Libre Baskerville', serif;
  font-size: 17px;
  font-weight: 700;
  color: var(--accent-strong);
  flex-shrink: 0;
  &--sm {
    width: 42px;
    height: 42px;
    font-size: 16px;
    background: var(--bg-subtle);
    border-color: var(--border-soft);
    color: var(--ink-muted);
    overflow: hidden;
  }
}

// ---- Account row extras -----------------------------------------------------
.lp-acc-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lp-acc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 2px;
}
// Chat / Group / Channel type chip in the label view rows.
.lp-type-pill {
  flex-shrink: 0;
  align-self: center;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  border: 1px solid var(--border-soft);
  color: var(--ink-muted);
  background: var(--bg-subtle);
}
.lp-type-pill--group   { color: #0f766e; background: var(--accent-tint); border-color: var(--accent); }
.lp-type-pill--channel { color: #5b2ecf; background: #f0ecfc; border-color: #cbb8f5; }
// Colour dot + drill-in chevron for the labels-overview rows.
.lp-label-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  align-self: center;
}
.lp-row-chev { color: var(--ink-muted); flex-shrink: 0; align-self: center; }
.lp-acc-count {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
  flex-shrink: 0;
}
// Multi-account overview: highlight account rows that have unread messages
// — same Telegram-blue + left accent bar + bold name pattern we use for
// individual chat rows (.chat-row--unread) so the two views feel consistent.
// Whether "unread" means DMs or channels is decided one level up — the
// listpane is fed the right map per view (CHATS vs CHANNELS & GROUPS).
.account-row--unread {
  background: rgba(34, 158, 217, 0.07);
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0; top: 6px; bottom: 6px;
    width: 3px;
    border-radius: 2px;
    background: linear-gradient(180deg, #229ED9, #0088CC);
  }
  .lp-acc-name {
    font-weight: 800;
    color: #0a4d6e;
  }
}

// ---- Chat context menu (position:fixed) -------------------------------------
.lp-ctx-wrap {
  position: fixed;
  z-index: 9999;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(31, 42, 42, 0.14);
  padding: 6px;
  min-width: 182px;
}
.lp-ctx-sub {
  background: var(--bg-subtle);
  border-radius: 8px;
  padding: 4px 0;
  margin: 2px 0;
}
.lp-ctx-sub-head {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-muted);
  padding: 4px 12px 5px;
}
// Agent rows in the assign submenu: ✓ marks the current assignee, the right-edge
// "+" toggles the agent as an ADDITIONAL participant on the chat.
.lp-ctx-agent-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.lp-ctx-agent-row--assigned {
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-weight: 700;
}
.lp-ctx-agent-check {
  width: 12px;
  flex-shrink: 0;
  color: var(--accent-strong);
  font-weight: 800;
}
.lp-ctx-agent-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lp-ctx-part-btn {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-muted);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
.lp-ctx-part-btn:hover {
  border-color: var(--accent-ring);
  color: var(--accent-strong);
}
.lp-ctx-part-btn--on {
  background: var(--accent-tint);
  border-color: var(--accent-ring);
  color: var(--accent-strong);
}
.lp-ctx-sub-hint {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
  padding: 3px 12px 5px;
}

// ---- Chat row sub-elements --------------------------------------------------
// .chat-row itself is styled globally in _fangate-theme.scss;
// these sub-elements live here because they're rendered inside this component.
.chat-row-body {
  flex: 1;
  min-width: 0;
}

// Chat/channel rows render as floating cards (white surface + soft shadow +
// rounded) on the cream list column — desktop AND mobile, with a SMALL gap so
// the dense desktop list doesn't lose too many visible rows. Scoped → [data-v]
// beats the global flat .chat-row. (Pill search + segmented tabs now live in the
// base .lp-search-input / .lp-tab rules above, so they apply at every width too.)
.chat-row {
  margin: 0 8px 5px;
  padding: 10px 12px;
  border: none;
  border-radius: 14px;
  background: var(--bg-panel);
  box-shadow: 0 1px 2px rgba(20, 22, 40, 0.05), 0 3px 9px rgba(20, 22, 40, 0.04);
  transition: transform 0.08s, box-shadow 0.12s;
  &:active { transform: scale(0.99); }
}
.chat-row--unread:not(.active)::before { top: 11px; bottom: 11px; }

// Unread emphasis: row gets a soft Telegram-blue background + a left bar.
// Telegram's accent is #229ED9 (sky/cyan) — instantly recognisable to anyone
// coming from the app. Selected row's active style wins over unread.
.chat-row--unread:not(.active) {
  background: rgba(34, 158, 217, 0.07);
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0; top: 6px; bottom: 6px;
    width: 3px;
    border-radius: 2px;
    background: linear-gradient(180deg, #229ED9, #0088CC);
  }
  .chat-row-name {
    font-weight: 800;
    color: #0a4d6e;
  }
  .chat-row-preview {
    color: var(--ink-medium);
    font-weight: 500;
  }
}

.chat-row-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chat-row-preview {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  margin-top: 1px;
  // Layout: preview text fills, delivery ticks pin to the right edge so they
  // sit on the same baseline as the last message rather than the timestamp.
  display: flex; align-items: center; gap: 6px;
}
.chat-row-preview-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
// Channels/Groups preview: cap at 2 lines (was unbounded → 3-4 lines) to keep
// each row compact. Overrides .chat-row-preview's display:flex with a line-clamp
// box; only used where the preview text sits directly in the row (no ticks).
.chat-row-preview--clamp2 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}
// "Draft:" prefix on chat rows with unsent text — italic + corporate green
// so it stands out from the regular muted preview.
.chat-row-draft {
  font-style: italic;
  font-weight: 700;
  color: #047857;
  margin-right: 4px;
}
// Mute indicator next to the contact name — subtle gray.
.chat-row-mute {
  color: var(--ink-muted);
  flex-shrink: 0;
  margin-left: 4px;
  opacity: 0.7;
}
.chat-row-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}
// Channels & Groups variant: the column also carries the GROUP/CHANNEL type
// chip below the unread pill. Push it slightly toward the right edge so it
// doesn't crowd the time stamp, and keep the column from growing wide
// enough to eat the chat title (which was truncating to "Testg…" before).
.chat-row-meta--stack {
  gap: 6px;
  padding-top: 2px;
  max-width: 78px;
}
.chat-row-time {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
}

// ---- Scroll container -------------------------------------------------------
.lp-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 6px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.lp-empty {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  text-align: center;
  padding: 24px 8px;
  font-style: italic;
}

// "Load more" button — pagination-light footer shown under a capped list.
.lp-load-more {
  display: block;
  width: calc(100% - 16px);
  margin: 6px 8px 12px;
  padding: 8px 12px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--bg-panel);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.lp-load-more:hover:not(:disabled) {
  background: var(--accent-tint);
  border-color: var(--accent-ring);
}
.lp-load-more:disabled { opacity: 0.55; cursor: default; }

// Activity / A–Z sort toggle in the label drill-in header.
.lp-label-sort {
  display: inline-flex;
  margin-left: auto;
  border: 1px solid var(--border-soft);
  border-radius: 7px;
  overflow: hidden;
}
.lp-label-sort-btn {
  padding: 3px 9px;
  border: none;
  background: var(--bg-panel);
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  cursor: pointer;
}
.lp-label-sort-btn.active {
  background: var(--accent-tint);
  color: var(--accent-strong);
}

// ---- Dropdown ---------------------------------------------------------------
.lp-dropdown {
  position: absolute;
  top: 30px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(31, 42, 42, 0.13);
  padding: 8px;
  z-index: 100;
  min-width: 170px;
}
.lp-dropdown-head {
  font-family: 'Inter', sans-serif;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-muted);
  padding: 4px 8px 5px;
}
.lp-dropdown-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-medium);
  transition: background 0.1s;
  &.active { color: var(--accent-strong); background: var(--accent-tint); }
  &:not(.active):hover { background: var(--bg-subtle); }
}
.lp-radio {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid var(--border-medium);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  &.checked { border-color: var(--accent); background: var(--accent); }
}

// ---- Checkbox (filter dropdown) ----------------------------------------------
.lp-check {
  width: 13px;
  height: 13px;
  border-radius: 3px;
  flex-shrink: 0;
  border: 1.5px solid var(--border-medium);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  &.checked { border-color: var(--accent); background: var(--accent); }
}
.lp-clear-btn {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: var(--ink-muted);
  padding: 0 !important;
  background: none;
  border: none;
  cursor: pointer;
  &:hover { color: var(--accent-strong); }
}

// ---- Filter chips -----------------------------------------------------------
.lp-filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 12px 6px;
  flex-shrink: 0;
}
.lp-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 5px 2px 8px;
  border-radius: 999px;
  background: var(--accent-tint);
  border: 1px solid var(--accent-ring);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-weight: 500;
}
.lp-chip-rm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  font-size: 13px;
  line-height: 1;
  color: var(--accent-strong);
  background: none;
  border: none;
  padding: 0 !important;
  cursor: pointer;
  opacity: 0.7;
  &:hover { opacity: 1; }
}

// ---- Avatar wrapper (for online dot) ----------------------------------------
.lp-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}
.lp-online-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #10b981;                       // green = online
  border: 2px solid var(--bg-middle, #f0f4f3);
  pointer-events: none;
  &--off {
    background: #cbd5d0;                     // gray = offline
  }
}

// ---- Label color dot (filter dropdown) --------------------------------------
.lp-lbl-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.lp-fl-divider {
  height: 1px;
  background: var(--border-soft);
  margin: 6px 4px 2px;
}

// ---- Delivery ticks ---------------------------------------------------------
.chat-row-time-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.lp-ticks {
  font-size: 11px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: -1px;
  &--sent      { color: #9ca3af; }
  &--delivered { color: #9ca3af; }
  &--read      { color: #3b82f6; }
}

// ---- Resolved section -------------------------------------------------------
.lp-resolved-sec {
  margin-top: 6px;
  border-top: 1px solid var(--border-soft);
  padding-top: 2px;
}
.lp-resolved-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px 5px;
  cursor: pointer;
  user-select: none;
  border-radius: 8px;
  &:hover { background: var(--bg-subtle); }
}
.lp-resolved-title {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent-strong);
}
.lp-resolved-count {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  background: var(--accent-soft);
  color: var(--accent-strong);
  padding: 1px 6px;
  border-radius: 999px;
}
.lp-resolved-chev {
  margin-left: auto;
  transition: transform 0.18s;
}
.lp-resolved-empty {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  text-align: center;
  padding: 8px 0 10px;
}

// ---- Channel icon avatar -----------------------------------------------------
.lp-channel-icon {
  background: var(--accent-tint) !important;
  border-color: var(--accent-ring) !important;
  color: var(--accent-strong) !important;
}

// Compact "Group" / "Channel" type chip rendered next to the chat name in
// the listpane row so the user doesn't have to decode the avatar icon to
// tell broadcast channels from groups.
.lp-ch-type {
  display: inline-flex; align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}
.lp-ch-type--group {
  background: rgba(13, 148, 136, 0.14);
  color: #0d9488;
}
.lp-ch-type--channel {
  background: rgba(91, 33, 182, 0.12);
  color: #5b21b6;
}

// ---- Channels empty state ----------------------------------------------------
.lp-channels-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 24px 16px;
  text-align: center;
}
.lp-channels-empty-icon {
  color: var(--ink-muted);
  opacity: 0.55;
}
.lp-channels-empty-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-medium);
  margin-top: 2px;
}
.lp-channels-empty-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  line-height: 1.4;
  max-width: 220px;
}

// ---- Chat row: kill text-caret blink + resolved dimming ---------------------
// Apply user-select:none + cursor:pointer to .chat-row AND every descendant
// so the I-beam can't appear on any inner text element.
:deep(.chat-row),
:deep(.chat-row *) {
  user-select: none;
  -webkit-user-select: none;
  cursor: pointer;
}
// ---- Chat row: name + inline label chips -----------------------------------
.chat-row-name-wrap {
  display: flex;
  align-items: baseline;   // labels sit on the name's text baseline
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  min-width: 0;            // allows .chat-row-name to ellipsize when long
}
.chat-row-name-wrap .chat-row-name {
  flex: 0 1 auto;          // name shrinks before pushing labels off
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat-row-lbl {
  display: inline-flex; align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  border: 1px solid var(--accent-ring);
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 600;
  line-height: 1.3;
  flex-shrink: 0;
}

// ---- Inline label picker for chat rows -------------------------------------
.lp-label-picker {
  position: fixed;
  z-index: 9999;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(11, 20, 20, 0.18);
  padding: 6px;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
}
.lp-label-picker-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px 8px;
  font-family: 'Inter', sans-serif;
  font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-muted);
}
.lp-label-picker-close {
  border: none; background: transparent;
  font-size: 14px; padding: 0 4px !important;
  color: var(--ink-muted); cursor: pointer;
  &:hover { color: var(--ink-strong); }
}
.lp-label-empty {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  padding: 8px 10px;
  font-style: italic;
  line-height: 1.5;
}
.lp-label-row {
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 500;
  cursor: pointer; text-align: left;
  transition: background 0.1s;
  &:hover { background: var(--bg-subtle); }
  &.active { color: var(--accent-strong); background: var(--accent-tint); }
}
.lp-label-check {
  width: 13px; height: 13px; border-radius: 3px;
  flex-shrink: 0;
  border: 1.5px solid var(--border-medium);
  background: transparent;
  display: flex; align-items: center; justify-content: center;
  &.checked { border-color: var(--accent); background: var(--accent); }
}
.lp-label-color-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}

.lp-chat-resolved {
  opacity: 0.75;
}
</style>
